import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Button,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputLabel,
  FormControl,
  Checkbox,
  FormGroup,
  Radio,
  RadioGroup,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Popover,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import AudiotrackIcon from '@mui/icons-material/Audiotrack'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import ImageIcon from '@mui/icons-material/Image'
import GroupsIcon from '@mui/icons-material/Groups'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import LinkIcon from '@mui/icons-material/Link'
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions'
import { useQuery } from '@tanstack/react-query'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import { TableRow, TableCell, TableHeader } from '@tiptap/extension-table'
import { BorderlessTable } from '../editor/extensions/BorderlessTable'
import { TipTapToolbar } from '../editor/panels/TipTapToolbar'
import { ResizableImage, ImageBubbleMenu } from '../editor/extensions/ResizableImage'
import { ImagePasteHandler } from '../editor/extensions/ImagePasteHandler'
import '../editor/styles/DescriptionPanel.css'
import { WorkflowNode, useWorkflowStore, getDefaultConditionConfig } from '@store/workflowStore'
import {
  WorkflowNodeType,
  WorkflowConditionType,
  NumpadInputStyle,
  NumpadConfig,
  CodexConfig,
  QrCodeConfig,
  QuestionAnswerConfig,
  QuestionAnswerType,
  AnswerOption,
  PoiLocationConfig,
  POI_MARKER_EMOJIS,
  PoiMarkerStyle,
  PoiCircleBorderStyle,
  PoiCircleFillStyle,
  RouteConfig,
  RouteArrowStyle,
  TextClueConfig,
  AudioConfig,
  VideoConfig,
  ImageConfig,
  FormattedMessageConfig,
  UnlockAdventureConfig,
  UnlockStyle,
  CriteriaBypass,
  GroupMembershipConfig,
  GiftRewardConfig,
  DelayConfig,
  DelayTimeUnit,
  SUPPORTED_AUDIO_MIME_TYPES,
  SUPPORTED_AUDIO_EXTENSIONS,
  MAX_AUDIO_FILE_SIZE,
  ALLOWED_VIDEO_HOSTS,
  validateVideoUrl,
  getNodeTypeLabel,
  getNodeTypeColor,
} from '@features/adventures/types/workflow.types'
import { getAdventureStatusLabel, getAdventureStatusColor, getCriteriaTypeIcon, AdventureMainCategory } from '@features/adventures/types/adventure-editor.types'
import { workflowDesignerApi } from '@api/endpoints/workflowDesigner'
import { adventuresApi } from '@api/endpoints/adventures'
import { socialApi } from '@api/endpoints/social'
import { rewardsApi } from '@api/endpoints/rewards'
import type { AdventureDto } from '@api/types/adventure'
import type { GroupDto } from '@api/types/social'
import type { GiftRewardTemplateDto } from '@api/types/rewards'
import { RouteNodePropertiesPanel } from './RouteNodePropertiesPanel'
import { GeofencePicker } from '@components/map/GeofencePicker'
import { MapContainer } from '@components/map/MapContainer'
import { Marker, Circle, Popup, useMapEvents, useMap, Polyline } from 'react-leaflet'
import L from 'leaflet'
import { MyLocation, CenterFocusStrong, Fullscreen, FullscreenExit } from '@mui/icons-material'
import { ApiService } from '@api/apiService'
import { MeasurementUnit } from '@/types/settings'
import { settingsApi } from '@api/endpoints/settings'
import { ConditionConfigPanel } from './conditions'
import { getConditionTypeLabel } from '@features/adventures/types/workflow.types'
import { useAdventureClues } from '../../hooks/useAdventureClues'
import type { ClueGateConfig } from '@features/adventures/types/workflow.types'
import { ImageUploadZone } from '@components/common/ImageUploadZone'
import { ImageFileSelectorDialog } from '../editor/media/ImageFileSelectorDialog'
import type { AdventureMediaFileDto } from '@api/types/adventureFiles'

// Available logical condition types for Decision nodes
// Organized: Free conditions first (grouped by category), then Pro conditions at bottom
const logicalConditionTypes = [
  // === FREE CONDITIONS ===
  // Weather (Free)
  { type: WorkflowConditionType.WeatherDayNight, label: 'Day/Night', requiresSubscription: false },
  { type: WorkflowConditionType.WeatherSunsetSunrise, label: 'Sunset/Sunrise', requiresSubscription: false },
  // Celestial (Free)
  { type: WorkflowConditionType.CelestialSolarNoon, label: 'Solar Noon', requiresSubscription: false },
  // Time
  { type: WorkflowConditionType.TimeSchedule, label: 'Time/Schedule', requiresSubscription: false },
  // Location
  { type: WorkflowConditionType.Geofence, label: 'Geofence Location', requiresSubscription: false },
  // Group
  { type: WorkflowConditionType.GroupMembership, label: 'Group Membership', requiresSubscription: false },
  // Adventure
  { type: WorkflowConditionType.AdventureCollectionCompletion, label: 'Collection Completion', requiresSubscription: false },
  { type: WorkflowConditionType.AdventureCompleted, label: 'Adventure Completed', requiresSubscription: false },
  // User stats
  { type: WorkflowConditionType.UserRank, label: 'User Rank', requiresSubscription: false },

  // === PRO CONDITIONS ===
  // Weather (Pro)
  { type: WorkflowConditionType.WeatherTemperature, label: 'Temperature', requiresSubscription: true },
  { type: WorkflowConditionType.WeatherHumidity, label: 'Humidity', requiresSubscription: true },
  { type: WorkflowConditionType.WeatherWindSpeed, label: 'Wind Speed', requiresSubscription: true },
  { type: WorkflowConditionType.WeatherWindDirection, label: 'Wind Direction', requiresSubscription: true },
  { type: WorkflowConditionType.WeatherCloudCover, label: 'Cloud Cover', requiresSubscription: true },
  { type: WorkflowConditionType.WeatherVisibility, label: 'Visibility', requiresSubscription: true },
  { type: WorkflowConditionType.WeatherCondition, label: 'Weather Condition', requiresSubscription: true },
  // Celestial (Pro)
  { type: WorkflowConditionType.CelestialTwilight, label: 'Twilight', requiresSubscription: true },
  { type: WorkflowConditionType.CelestialMoonRiseSet, label: 'Moonrise/Moonset', requiresSubscription: true },
  { type: WorkflowConditionType.CelestialMoonPhase, label: 'Moon Phase', requiresSubscription: true },
  { type: WorkflowConditionType.CelestialMoonAge, label: 'Moon Age', requiresSubscription: true },
]

// Extended emoji list for POI markers - focused on map/location-appropriate icons
const EXTENDED_POI_EMOJIS = [
  // Map & Navigation
  '📍', '📌', '🗺️', '🧭', '🏁', '🚩', '🏴', '⛳', '🎯', '➡️', '⬆️', '⬇️', '⬅️', '↗️', '↘️', '↙️', '↖️', '🔄',
  // Buildings - Residential & Commercial
  '🏠', '🏡', '🏘️', '🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪', '🏫', '🏬', '🏭', '🏗️', '🏚️',
  // Buildings - Cultural & Religious
  '🏛️', '🏰', '🏯', '⛪', '🕌', '🕍', '⛩️', '🗼', '🗽', '🗿',
  // Buildings - Entertainment & Sports
  '🏟️', '🎪', '🎭', '🎬', '🎰', '🎢', '🎡', '🎠',
  // Outdoors & Recreation
  '⛺', '🏕️', '🏖️', '🏜️', '🏝️', '🏞️', '🛖',
  // Nature - Trees & Plants
  '🌲', '🌳', '🌴', '🌵', '🌾', '🌿', '☘️', '🍀', '🌱', '🪴', '🎋', '🎍', '🪵', '🪨',
  // Nature - Flowers
  '🌸', '🌺', '🌻', '🌼', '🌷', '🌹', '🪻', '🪷', '💐',
  // Nature - Landscapes & Weather
  '🏔️', '⛰️', '🌋', '🗻', '🏜️', '🌊', '💧', '🌀', '☀️', '🌙', '⭐', '🌟', '🌈', '☁️', '⛅', '🌧️', '⛈️', '❄️', '🔥',
  // Food & Dining
  '🍽️', '🍴', '🥢', '🍕', '🍔', '🍟', '🌭', '🥪', '🌮', '🌯', '🍜', '🍝', '🍣', '🍱', '🥡', '🍳', '🥐', '🍞', '🥖', '🎂', '🍰', '🧁', '🍩', '🍪',
  // Drinks & Beverages
  '☕', '🍵', '🧃', '🥤', '🍺', '🍻', '🍷', '🍸', '🍹', '🧊', '🍶',
  // Facilities & Services
  '🚻', '🚹', '🚺', '🚼', '♿', '🛗', '🚰', '🚮', '🛂', '🛃', '🛄', '🛅', '⚠️', 'ℹ️', '🅿️', '🈳', '🆓',
  // Transportation - Vehicles
  '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🛻', '🚚', '🚜', '🛵', '🏍️', '🚲', '🛴', '🦽', '🦼',
  // Transportation - Air & Water
  '✈️', '🛩️', '🚁', '🛶', '⛵', '🚤', '🛥️', '🚢', '⚓', '⛽',
  // Transportation - Rail & Public
  '🚂', '🚃', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🛤️', '🚏',
  // Sports & Activities
  '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸', '🏒', '🏑', '🥅', '⛳', '🏹', '🎣', '🤿', '🥊', '🥋', '🎿', '🛷', '🏂', '⛸️', '🏋️', '🚴', '🏊', '🧗', '🏇', '🛹', '🛼',
  // Entertainment & Culture
  '🎨', '🎭', '🎪', '🎬', '🎤', '🎧', '🎸', '🎹', '🎷', '🎺', '🎻', '🥁', '🎮', '🎲', '🎰', '🧩', '📷', '📹', '🔭', '🔬',
  // Shopping & Commerce
  '🛒', '🛍️', '💳', '💰', '💵', '🏧', '💎', '👔', '👗', '👟', '👜', '🎁',
  // Medical & Emergency
  '🏥', '💊', '💉', '🩺', '🩹', '🚑', '🆘', '⚕️',
  // Education & Office
  '📚', '📖', '📝', '✏️', '📐', '📏', '🎓', '🏫', '📬', '📮',
  // Tools & Objects
  '🔧', '🔨', '⚒️', '🛠️', '🔩', '⚙️', '🔑', '🗝️', '🔒', '🔓', '📦', '🧰', '🪜', '🧲', '💡', '🔦', '🕯️',
  // Animals (for zoos, parks, wildlife areas)
  '🐕', '🐈', '🐎', '🐄', '🐑', '🐐', '🐖', '🐓', '🦆', '🦢', '🦅', '🦉', '🐻', '🦁', '🐯', '🐘', '🦒', '🦓', '🐒', '🦈', '🐬', '🐳', '🐠', '🦋', '🐝', '🐞',
  // Symbols & Markers
  '⭕', '❌', '✅', '❗', '❓', '‼️', '⁉️', '💯', '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔶', '🔷', '🔸', '🔹', '▶️', '⏸️', '⏹️', '🔔', '🔕',
  // Numbers (for numbered waypoints)
  '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '#️⃣', '*️⃣',
]

// Emoji keyword mapping for search - POI focused
const EMOJI_KEYWORDS: Record<string, string[]> = {
  // Map & Navigation
  '📍': ['pin', 'location', 'marker', 'place', 'spot', 'point'],
  '📌': ['pin', 'pushpin', 'tack', 'location', 'marker'],
  '🗺️': ['map', 'world', 'travel', 'geography', 'atlas'],
  '🧭': ['compass', 'navigation', 'direction', 'north'],
  '🏁': ['finish', 'race', 'flag', 'checkered', 'end', 'start'],
  '🚩': ['flag', 'red', 'marker', 'triangular'],
  '🏴': ['flag', 'black', 'pirate'],
  '⛳': ['golf', 'flag', 'hole', 'course'],
  '🎯': ['target', 'dart', 'bullseye', 'goal', 'objective'],
  '➡️': ['arrow', 'right', 'direction', 'next'],
  '⬆️': ['arrow', 'up', 'direction', 'north'],
  '⬇️': ['arrow', 'down', 'direction', 'south'],
  '⬅️': ['arrow', 'left', 'direction', 'back'],
  // Buildings - Residential & Commercial
  '🏠': ['house', 'home', 'building', 'residence'],
  '🏡': ['house', 'home', 'garden', 'yard'],
  '🏘️': ['houses', 'neighborhood', 'suburb', 'residential'],
  '🏢': ['office', 'building', 'work', 'corporate'],
  '🏣': ['post', 'office', 'mail', 'japanese'],
  '🏤': ['post', 'office', 'mail', 'european'],
  '🏥': ['hospital', 'medical', 'health', 'emergency', 'clinic'],
  '🏦': ['bank', 'money', 'finance', 'building'],
  '🏨': ['hotel', 'lodging', 'stay', 'accommodation'],
  '🏩': ['hotel', 'love', 'motel'],
  '🏪': ['store', 'shop', 'convenience', 'retail', 'market'],
  '🏫': ['school', 'education', 'building', 'learning'],
  '🏬': ['department', 'store', 'mall', 'shopping'],
  '🏭': ['factory', 'industrial', 'manufacturing'],
  '🏗️': ['construction', 'building', 'crane', 'site'],
  '🏚️': ['abandoned', 'house', 'derelict', 'old'],
  // Buildings - Cultural & Religious
  '🏛️': ['museum', 'building', 'classical', 'government', 'historical'],
  '🏰': ['castle', 'palace', 'fortress', 'medieval'],
  '🏯': ['castle', 'japan', 'japanese', 'palace'],
  '⛪': ['church', 'religion', 'christian', 'chapel'],
  '🕌': ['mosque', 'islam', 'muslim', 'religion'],
  '🕍': ['synagogue', 'jewish', 'temple', 'religion'],
  '⛩️': ['shrine', 'japan', 'torii', 'shinto'],
  '🗼': ['tower', 'tokyo', 'landmark'],
  '🗽': ['statue', 'liberty', 'nyc', 'landmark', 'monument'],
  '🗿': ['moai', 'statue', 'easter', 'island', 'monument'],
  // Buildings - Entertainment
  '🏟️': ['stadium', 'arena', 'sports', 'venue'],
  '🎪': ['circus', 'tent', 'carnival', 'fair'],
  '🎭': ['theater', 'drama', 'performing', 'arts', 'masks'],
  '🎬': ['movie', 'film', 'cinema', 'theater', 'studio'],
  '🎰': ['casino', 'slot', 'gambling', 'vegas'],
  '🎢': ['rollercoaster', 'amusement', 'theme', 'park'],
  '🎡': ['ferris', 'wheel', 'amusement', 'fair'],
  '🎠': ['carousel', 'merry', 'go', 'round', 'amusement'],
  // Outdoors & Recreation
  '⛺': ['tent', 'camp', 'camping', 'outdoor'],
  '🏕️': ['camping', 'tent', 'outdoor', 'campsite'],
  '🏖️': ['beach', 'sand', 'umbrella', 'coast'],
  '🏜️': ['desert', 'sand', 'dune', 'dry'],
  '🏝️': ['island', 'beach', 'tropical', 'paradise'],
  '🏞️': ['park', 'national', 'nature', 'scenic'],
  '🛖': ['hut', 'cabin', 'shelter'],
  // Nature - Trees & Plants
  '🌲': ['tree', 'pine', 'evergreen', 'forest', 'conifer'],
  '🌳': ['tree', 'deciduous', 'nature', 'oak'],
  '🌴': ['palm', 'tree', 'tropical', 'beach'],
  '🌵': ['cactus', 'desert', 'succulent'],
  '🌾': ['rice', 'grain', 'wheat', 'farm', 'agriculture'],
  '🌿': ['herb', 'plant', 'leaf', 'green'],
  '☘️': ['shamrock', 'clover', 'irish', 'luck'],
  '🍀': ['clover', 'lucky', 'four', 'leaf'],
  '🌱': ['seedling', 'plant', 'grow', 'sprout'],
  '🪴': ['plant', 'pot', 'houseplant'],
  '🪵': ['wood', 'log', 'timber', 'lumber'],
  '🪨': ['rock', 'stone', 'boulder'],
  // Nature - Flowers
  '🌸': ['blossom', 'cherry', 'flower', 'spring', 'sakura'],
  '🌺': ['flower', 'hibiscus', 'tropical'],
  '🌻': ['sunflower', 'flower', 'yellow'],
  '🌼': ['flower', 'blossom', 'daisy'],
  '🌷': ['tulip', 'flower', 'spring'],
  '🌹': ['rose', 'flower', 'red', 'garden'],
  '💐': ['bouquet', 'flowers', 'arrangement'],
  // Nature - Landscapes
  '🏔️': ['mountain', 'snow', 'peak', 'alpine'],
  '⛰️': ['mountain', 'hill', 'peak'],
  '🌋': ['volcano', 'eruption', 'mountain', 'lava'],
  '🗻': ['fuji', 'mountain', 'japan', 'snow'],
  '🌊': ['wave', 'ocean', 'sea', 'water', 'beach'],
  '💧': ['water', 'drop', 'rain', 'fountain'],
  '☀️': ['sun', 'sunny', 'bright', 'weather', 'day'],
  '🌙': ['moon', 'night', 'crescent'],
  '⭐': ['star', 'favorite', 'important', 'night'],
  '🌟': ['star', 'glow', 'shine', 'glowing'],
  '🌈': ['rainbow', 'colorful', 'weather'],
  '🔥': ['fire', 'hot', 'flame', 'campfire'],
  '❄️': ['snow', 'cold', 'winter', 'ice'],
  // Food & Dining
  '🍽️': ['restaurant', 'dining', 'plate', 'food', 'eat'],
  '🍴': ['restaurant', 'dining', 'utensils', 'food'],
  '🥢': ['chopsticks', 'asian', 'food', 'chinese', 'japanese'],
  '🍕': ['pizza', 'food', 'italian', 'restaurant'],
  '🍔': ['burger', 'food', 'hamburger', 'fast', 'restaurant'],
  '🍟': ['fries', 'food', 'fast', 'mcdonalds'],
  '🌭': ['hotdog', 'food', 'fast', 'sausage'],
  '🥪': ['sandwich', 'food', 'sub', 'deli'],
  '🌮': ['taco', 'mexican', 'food'],
  '🌯': ['burrito', 'mexican', 'food', 'wrap'],
  '🍜': ['noodles', 'ramen', 'asian', 'food', 'soup'],
  '🍝': ['pasta', 'spaghetti', 'italian', 'food'],
  '🍣': ['sushi', 'japanese', 'food', 'fish'],
  '🍱': ['bento', 'japanese', 'food', 'box'],
  '🍳': ['breakfast', 'eggs', 'cooking', 'brunch'],
  '🍞': ['bread', 'bakery', 'food'],
  '🎂': ['cake', 'birthday', 'bakery', 'dessert'],
  '🍰': ['cake', 'dessert', 'bakery', 'slice'],
  '🍩': ['donut', 'doughnut', 'bakery', 'dessert'],
  '🍪': ['cookie', 'bakery', 'dessert', 'snack'],
  // Drinks & Beverages
  '☕': ['coffee', 'cafe', 'drink', 'espresso', 'latte'],
  '🍵': ['tea', 'drink', 'green', 'matcha'],
  '🧃': ['juice', 'drink', 'box'],
  '🥤': ['drink', 'soda', 'cup', 'beverage'],
  '🍺': ['beer', 'drink', 'bar', 'pub', 'alcohol'],
  '🍻': ['beer', 'cheers', 'bar', 'pub'],
  '🍷': ['wine', 'drink', 'alcohol', 'vineyard'],
  '🍸': ['cocktail', 'drink', 'bar', 'martini'],
  '🍹': ['tropical', 'drink', 'cocktail', 'bar'],
  // Facilities & Services
  '🚻': ['restroom', 'bathroom', 'toilet', 'wc', 'facilities'],
  '🚹': ['mens', 'restroom', 'bathroom', 'male'],
  '🚺': ['womens', 'restroom', 'bathroom', 'female'],
  '🚼': ['baby', 'changing', 'nursery', 'family'],
  '♿': ['accessible', 'wheelchair', 'disability', 'handicap'],
  '🛗': ['elevator', 'lift', 'accessibility'],
  '🚰': ['water', 'fountain', 'potable', 'drinking'],
  '🚮': ['trash', 'litter', 'garbage', 'bin'],
  '⚠️': ['warning', 'caution', 'alert', 'danger'],
  'ℹ️': ['information', 'info', 'help', 'desk'],
  '🅿️': ['parking', 'car', 'lot', 'garage'],
  '🆓': ['free', 'gratis', 'complimentary'],
  // Transportation
  '🚗': ['car', 'vehicle', 'drive', 'auto'],
  '🚕': ['taxi', 'cab', 'car', 'ride'],
  '🚙': ['suv', 'car', 'vehicle', 'rv'],
  '🚌': ['bus', 'transit', 'public', 'transport'],
  '🚓': ['police', 'car', 'emergency', 'cop'],
  '🚑': ['ambulance', 'emergency', 'medical', 'hospital'],
  '🚒': ['fire', 'truck', 'emergency', 'firefighter'],
  '🚐': ['van', 'minibus', 'vehicle'],
  '🛻': ['truck', 'pickup', 'vehicle'],
  '🚚': ['truck', 'delivery', 'moving'],
  '🚜': ['tractor', 'farm', 'agriculture'],
  '🛵': ['scooter', 'moped', 'vespa'],
  '🏍️': ['motorcycle', 'bike', 'motorbike'],
  '🚲': ['bike', 'bicycle', 'cycle', 'cycling'],
  '🛴': ['scooter', 'kick', 'electric'],
  '🦽': ['wheelchair', 'manual', 'accessibility'],
  '🦼': ['wheelchair', 'motorized', 'accessibility'],
  '✈️': ['plane', 'airplane', 'flight', 'airport', 'travel'],
  '🛩️': ['plane', 'small', 'aircraft', 'private'],
  '🚁': ['helicopter', 'chopper', 'helipad'],
  '🛶': ['canoe', 'kayak', 'paddle', 'boat'],
  '⛵': ['boat', 'sail', 'sailboat', 'marina'],
  '🚤': ['speedboat', 'boat', 'marina'],
  '🚢': ['ship', 'boat', 'cruise', 'ferry', 'port'],
  '⚓': ['anchor', 'port', 'harbor', 'marina', 'dock'],
  '⛽': ['gas', 'fuel', 'station', 'petrol'],
  '🚂': ['train', 'locomotive', 'steam', 'railway'],
  '🚆': ['train', 'rail', 'railway', 'station'],
  '🚇': ['metro', 'subway', 'underground', 'tube'],
  '🚉': ['station', 'train', 'railway', 'transit'],
  '🚏': ['bus', 'stop', 'transit', 'station'],
  '🛤️': ['railway', 'tracks', 'train'],
  // Sports & Activities
  '⚽': ['soccer', 'football', 'ball', 'sport', 'field'],
  '🏀': ['basketball', 'ball', 'sport', 'court'],
  '🏈': ['football', 'american', 'sport', 'field'],
  '⚾': ['baseball', 'ball', 'sport', 'field'],
  '🎾': ['tennis', 'ball', 'sport', 'court'],
  '🏐': ['volleyball', 'ball', 'sport', 'beach'],
  '🏒': ['hockey', 'ice', 'sport', 'rink'],
  '🥅': ['goal', 'net', 'hockey', 'soccer'],
  '🏹': ['archery', 'bow', 'arrow', 'range'],
  '🎣': ['fishing', 'fish', 'rod', 'pier'],
  '🤿': ['diving', 'snorkel', 'scuba', 'underwater'],
  '🥊': ['boxing', 'fight', 'gym', 'sport'],
  '🥋': ['martial', 'arts', 'karate', 'judo', 'dojo'],
  '🎿': ['ski', 'skiing', 'winter', 'snow', 'slope'],
  '🛷': ['sled', 'sledding', 'winter', 'snow'],
  '🏂': ['snowboard', 'winter', 'snow', 'slope'],
  '⛸️': ['ice', 'skating', 'rink', 'winter'],
  '🏋️': ['gym', 'fitness', 'weights', 'workout'],
  '🚴': ['cycling', 'bike', 'bicycle', 'trail'],
  '🏊': ['swimming', 'pool', 'swim', 'aquatic'],
  '🧗': ['climbing', 'rock', 'wall', 'bouldering'],
  '🏇': ['horse', 'racing', 'equestrian', 'track'],
  '🛹': ['skateboard', 'skate', 'park'],
  // Entertainment & Culture
  '🎨': ['art', 'paint', 'palette', 'gallery', 'museum'],
  '🎤': ['microphone', 'sing', 'karaoke', 'music', 'concert'],
  '🎧': ['headphones', 'music', 'audio', 'dj'],
  '🎸': ['guitar', 'music', 'rock', 'concert'],
  '🎹': ['piano', 'keyboard', 'music'],
  '🎷': ['saxophone', 'jazz', 'music'],
  '🎺': ['trumpet', 'brass', 'music'],
  '🎻': ['violin', 'orchestra', 'music', 'classical'],
  '🥁': ['drums', 'percussion', 'music'],
  '🎮': ['game', 'video', 'arcade', 'gaming'],
  '🎲': ['dice', 'game', 'board', 'casino'],
  '🧩': ['puzzle', 'game', 'escape', 'room'],
  '📷': ['camera', 'photo', 'photography', 'viewpoint'],
  '📹': ['video', 'camera', 'recording'],
  '🔭': ['telescope', 'observatory', 'astronomy', 'star'],
  '🔬': ['microscope', 'science', 'lab', 'research'],
  // Shopping & Commerce
  '🛒': ['shopping', 'cart', 'grocery', 'supermarket', 'store'],
  '🛍️': ['shopping', 'bags', 'mall', 'retail'],
  '💳': ['credit', 'card', 'payment', 'atm'],
  '💰': ['money', 'bag', 'bank', 'cash'],
  '💵': ['money', 'dollar', 'cash', 'currency'],
  '🏧': ['atm', 'cash', 'bank', 'money'],
  '💎': ['gem', 'diamond', 'jewel', 'jewelry', 'store'],
  '🎁': ['gift', 'present', 'shop', 'store'],
  // Medical & Emergency
  '💊': ['pharmacy', 'medicine', 'drug', 'pill'],
  '💉': ['vaccine', 'injection', 'medical', 'clinic'],
  '🩺': ['doctor', 'medical', 'clinic', 'health'],
  '🩹': ['bandage', 'first', 'aid', 'medical'],
  '🆘': ['emergency', 'sos', 'help', 'rescue'],
  '⚕️': ['medical', 'health', 'hospital', 'doctor'],
  // Education & Office
  '📚': ['library', 'books', 'reading', 'education'],
  '📖': ['book', 'reading', 'library', 'study'],
  '📝': ['note', 'writing', 'memo', 'office'],
  '✏️': ['pencil', 'write', 'school', 'office'],
  '🎓': ['graduation', 'university', 'college', 'school', 'education'],
  '📬': ['mailbox', 'post', 'mail', 'letter'],
  '📮': ['postbox', 'mail', 'post', 'letter'],
  // Tools & Objects
  '🔧': ['wrench', 'tool', 'repair', 'mechanic'],
  '🔨': ['hammer', 'tool', 'construction', 'build'],
  '🛠️': ['tools', 'repair', 'workshop', 'maintenance'],
  '⚙️': ['gear', 'settings', 'mechanical', 'engineering'],
  '🔑': ['key', 'unlock', 'lock', 'access'],
  '🗝️': ['key', 'old', 'vintage', 'antique'],
  '🔒': ['lock', 'secure', 'closed', 'private'],
  '🔓': ['unlock', 'open', 'access'],
  '📦': ['box', 'package', 'delivery', 'shipping'],
  '🧰': ['toolbox', 'tools', 'repair'],
  '💡': ['light', 'idea', 'bulb', 'lamp'],
  '🔦': ['flashlight', 'torch', 'light'],
  // Animals (for zoos, parks, etc.)
  '🐕': ['dog', 'pet', 'park', 'animal'],
  '🐈': ['cat', 'pet', 'animal'],
  '🐎': ['horse', 'stable', 'equestrian', 'ranch'],
  '🐄': ['cow', 'farm', 'dairy', 'ranch'],
  '🐑': ['sheep', 'farm', 'wool', 'ranch'],
  '🐐': ['goat', 'farm', 'petting', 'zoo'],
  '🐖': ['pig', 'farm', 'ranch'],
  '🐓': ['rooster', 'chicken', 'farm'],
  '🦆': ['duck', 'pond', 'bird', 'park'],
  '🦢': ['swan', 'pond', 'bird', 'lake'],
  '🦅': ['eagle', 'bird', 'wildlife', 'raptor'],
  '🦉': ['owl', 'bird', 'wildlife', 'nocturnal'],
  '🐻': ['bear', 'zoo', 'wildlife', 'forest'],
  '🦁': ['lion', 'zoo', 'safari', 'wildlife'],
  '🐯': ['tiger', 'zoo', 'wildlife'],
  '🐘': ['elephant', 'zoo', 'safari', 'wildlife'],
  '🦒': ['giraffe', 'zoo', 'safari', 'wildlife'],
  '🦓': ['zebra', 'zoo', 'safari', 'wildlife'],
  '🐒': ['monkey', 'zoo', 'primate', 'wildlife'],
  '🦈': ['shark', 'aquarium', 'ocean', 'marine'],
  '🐬': ['dolphin', 'aquarium', 'ocean', 'marine'],
  '🐳': ['whale', 'ocean', 'marine', 'watching'],
  '🐠': ['fish', 'aquarium', 'tropical', 'marine'],
  '🦋': ['butterfly', 'garden', 'nature', 'insect'],
  '🐝': ['bee', 'honey', 'garden', 'pollinator'],
  '🐞': ['ladybug', 'garden', 'insect', 'nature'],
  // Symbols & Markers
  '⭕': ['circle', 'marker', 'point'],
  '❌': ['cross', 'no', 'wrong', 'closed'],
  '✅': ['check', 'done', 'complete', 'yes', 'verified'],
  '❗': ['exclamation', 'important', 'alert', 'warning'],
  '❓': ['question', 'help', 'unknown', 'info'],
  '💯': ['hundred', 'perfect', 'complete'],
  '🔴': ['red', 'circle', 'dot', 'stop'],
  '🟠': ['orange', 'circle', 'dot'],
  '🟡': ['yellow', 'circle', 'dot', 'caution'],
  '🟢': ['green', 'circle', 'dot', 'go'],
  '🔵': ['blue', 'circle', 'dot'],
  '🟣': ['purple', 'circle', 'dot'],
  '⚫': ['black', 'circle', 'dot'],
  '⚪': ['white', 'circle', 'dot'],
  '🔔': ['bell', 'notification', 'alert', 'ring'],
  // Numbers
  '1️⃣': ['one', 'first', 'number', '1'],
  '2️⃣': ['two', 'second', 'number', '2'],
  '3️⃣': ['three', 'third', 'number', '3'],
  '4️⃣': ['four', 'fourth', 'number', '4'],
  '5️⃣': ['five', 'fifth', 'number', '5'],
  '6️⃣': ['six', 'sixth', 'number', '6'],
  '7️⃣': ['seven', 'seventh', 'number', '7'],
  '8️⃣': ['eight', 'eighth', 'number', '8'],
  '9️⃣': ['nine', 'ninth', 'number', '9'],
  '🔟': ['ten', 'tenth', 'number', '10'],
}

// Generate unique GUID for conditions
function generateConditionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback UUID v4 implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Helper to check if node is a Number Pad node (now uses nodeType)
function isNumpadNode(node: WorkflowNode): boolean {
  return node.data.nodeType === WorkflowNodeType.NumericInput
}

// Helper to check if node is a Codex node (A-Z letter roller)
function isCodexNode(node: WorkflowNode): boolean {
  return node.data.nodeType === WorkflowNodeType.CodexInput
}

// Helper to check if node is a QR Scanner node (now uses nodeType)
function isQrScannerNode(node: WorkflowNode): boolean {
  return node.data.nodeType === WorkflowNodeType.QRCode
}

// Helper to check if node is a Question/Answer node (now uses nodeType)
function isQuestionAnswerNode(node: WorkflowNode): boolean {
  return node.data.nodeType === WorkflowNodeType.QuestionAnswer
}

// Helper to check if node is a Moderator Approval node (now uses nodeType)
function isModeratorApprovalNode(node: WorkflowNode): boolean {
  return node.data.nodeType === WorkflowNodeType.ModeratorApproval
}

// Generate unique GUID for answer options
function generateOptionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback UUID v4 implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

interface NodePropertiesPanelProps {
  node: WorkflowNode
  adventureId: string
  onClose: () => void
  isReadOnly?: boolean
  mainCategory?: AdventureMainCategory
}

interface LocationItemOption {
  id: string
  name: string
  quickDescription?: string
}

// POI Map helpers for the location dialog
// Create emoji-based marker icon for POI locations
const createPoiEmojiMarkerIcon = (emoji: string, isActive: boolean = false) => {
  return new L.DivIcon({
    className: 'poi-emoji-marker',
    html: `
      <div style="
        font-size: ${isActive ? '32px' : '24px'};
        text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        line-height: 1;
        ${isActive ? 'filter: drop-shadow(0 0 4px #2196F3);' : ''}
      ">
        ${emoji}
      </div>
    `,
    iconSize: isActive ? [32, 32] : [24, 24],
    iconAnchor: isActive ? [16, 16] : [12, 12],
    popupAnchor: [0, isActive ? -16 : -12],
  })
}

// Hidden location marker icon (ghosted red X for editor preview)
const createHiddenLocationIcon = (isActive: boolean = false) => {
  return new L.DivIcon({
    className: 'hidden-location-marker',
    html: `
      <div style="
        font-size: ${isActive ? '24px' : '18px'};
        color: #ef5350;
        opacity: ${isActive ? '0.7' : '0.4'};
        text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        line-height: 1;
        ${isActive ? 'filter: drop-shadow(0 0 4px #ef5350);' : ''}
      ">✕</div>
    `,
    iconSize: isActive ? [24, 24] : [18, 18],
    iconAnchor: isActive ? [12, 12] : [9, 9],
    popupAnchor: [0, isActive ? -12 : -9],
  })
}

// Current editing location crosshair marker (distinctive circle with crosshairs)
const createCurrentEditingMarkerIcon = () => {
  return new L.DivIcon({
    className: 'current-editing-marker',
    html: `
      <div style="
        position: relative;
        width: 40px;
        height: 40px;
      ">
        <!-- Outer pulsing ring -->
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          width: 40px;
          height: 40px;
          border: 3px solid #00BCD4;
          border-radius: 50%;
          animation: pulse-ring 1.5s ease-out infinite;
          opacity: 0.6;
        "></div>
        <!-- Inner circle -->
        <div style="
          position: absolute;
          top: 8px;
          left: 8px;
          width: 24px;
          height: 24px;
          border: 3px solid #00BCD4;
          border-radius: 50%;
          background: rgba(0, 188, 212, 0.15);
        "></div>
        <!-- Horizontal crosshair -->
        <div style="
          position: absolute;
          top: 18px;
          left: 4px;
          width: 32px;
          height: 3px;
          background: #00BCD4;
        "></div>
        <!-- Vertical crosshair -->
        <div style="
          position: absolute;
          top: 4px;
          left: 18px;
          width: 3px;
          height: 32px;
          background: #00BCD4;
        "></div>
        <!-- Center dot -->
        <div style="
          position: absolute;
          top: 16px;
          left: 16px;
          width: 8px;
          height: 8px;
          background: #00BCD4;
          border-radius: 50%;
          box-shadow: 0 0 6px #00BCD4;
        "></div>
      </div>
      <style>
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.4); opacity: 0; }
        }
      </style>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  })
}

// Create arrow icon for route direction
const createRouteArrowIcon = (color: string, rotation: number) => {
  return new L.DivIcon({
    className: 'route-arrow-marker',
    html: `
      <div style="
        transform: rotate(${rotation}deg);
        color: ${color};
        font-size: 14px;
        line-height: 1;
        text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        opacity: 0.6;
      ">▶</div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  })
}

// Calculate bearing between two points for arrow rotation
const calculateRouteBearing = (start: [number, number], end: [number, number]): number => {
  const startLat = start[0] * Math.PI / 180
  const startLng = start[1] * Math.PI / 180
  const endLat = end[0] * Math.PI / 180
  const endLng = end[1] * Math.PI / 180

  const dLng = endLng - startLng
  const x = Math.sin(dLng) * Math.cos(endLat)
  const y = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng)

  let bearing = Math.atan2(x, y) * 180 / Math.PI
  return (bearing + 360) % 360 - 90 // Adjust for arrow pointing right
}

// Route data type for POI dialog map rendering
interface PoiDialogRouteData {
  id: string
  coordinates: [number, number][]
  color: string
  style: 'solid' | 'dashed' | 'dotted'
  opacity: number
  arrowStyle: RouteArrowStyle
}

// Map click handler component
function PoiMapClickHandler({ onLocationSelect }: { onLocationSelect: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng)
    },
  })
  return null
}

// Component to fit bounds to all POI markers
function PoiMapBoundsController({
  poiLocations,
  currentLocation
}: {
  poiLocations: Array<{ latitude: number; longitude: number }>
  currentLocation?: { latitude: number; longitude: number }
}) {
  const map = useMap()

  useEffect(() => {
    const points: L.LatLngExpression[] = []

    poiLocations.forEach(poi => {
      points.push([poi.latitude, poi.longitude])
    })

    if (currentLocation && currentLocation.latitude && currentLocation.longitude) {
      points.push([currentLocation.latitude, currentLocation.longitude])
    }

    if (points.length > 1) {
      const bounds = L.latLngBounds(points)
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
    } else if (points.length === 1) {
      map.setView(points[0], 13)
    }
  }, []) // Only run once on mount

  return null
}

// Component to center map on a specific location when triggered
function PoiMapCenterController({
  centerTarget
}: {
  centerTarget: { latitude: number; longitude: number; trigger: number } | null
}) {
  const map = useMap()

  useEffect(() => {
    if (centerTarget && centerTarget.latitude !== 0 && centerTarget.longitude !== 0) {
      map.setView([centerTarget.latitude, centerTarget.longitude], 16, { animate: true })
    }
  }, [centerTarget?.trigger, map])

  return null
}

// Condition types to hide for Trail adventures
const TRAIL_HIDDEN_CONDITIONS = new Set([
  WorkflowConditionType.CelestialSolarNoon,
  WorkflowConditionType.TimeSchedule,
  WorkflowConditionType.GroupMembership,
  WorkflowConditionType.AdventureCollectionCompletion,
  WorkflowConditionType.AdventureCompleted,
  WorkflowConditionType.UserRank,
  WorkflowConditionType.WeatherHumidity,
  WorkflowConditionType.WeatherWindSpeed,
  WorkflowConditionType.WeatherVisibility,
  WorkflowConditionType.WeatherCondition,
  WorkflowConditionType.CelestialTwilight,
  WorkflowConditionType.CelestialMoonRiseSet,
  WorkflowConditionType.CelestialMoonPhase,
  WorkflowConditionType.CelestialMoonAge,
])

export function NodePropertiesPanel({ node, adventureId, onClose, isReadOnly = false, mainCategory }: NodePropertiesPanelProps) {
  const { updateNode, deleteNode, nodes, edges } = useWorkflowStore()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // Filter logical condition types for Trail adventures
  const filteredLogicalConditionTypes = useMemo(() => {
    if (mainCategory === AdventureMainCategory.Trails) {
      return logicalConditionTypes.filter(c => !TRAIL_HIDDEN_CONDITIONS.has(c.type))
    }
    return logicalConditionTypes
  }, [mainCategory])

  const [label, setLabel] = useState(node.data.label)
  const [description, setDescription] = useState(node.data.description || '')
  const [isRequired, setIsRequired] = useState(node.data.isRequired)
  const [loopCooldownSeconds, setLoopCooldownSeconds] = useState(
    node.data.loopCooldownSeconds || 30
  )
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    node.data.locationItemId || null
  )
  const [selectedLoopTargetId, setSelectedLoopTargetId] = useState<string | null>(
    node.data.loopTargetNodeId || null
  )
  const [maxLoops, setMaxLoops] = useState<number | undefined>(node.data.maxLoops)
  const [timeBonusSeconds, setTimeBonusSeconds] = useState(node.data.timeBonusSeconds ?? 0)

  // Delay configuration state
  const [delayDuration, setDelayDuration] = useState(node.data.delayConfig?.duration ?? 5)
  const [delayUnit, setDelayUnit] = useState<DelayTimeUnit>(node.data.delayConfig?.unit ?? DelayTimeUnit.Minutes)

  // Numpad configuration state
  const [digitCount, setDigitCount] = useState(node.data.numpadConfig?.digitCount ?? 4)
  const [inputStyle, setInputStyle] = useState<NumpadInputStyle>(
    node.data.numpadConfig?.inputStyle ?? NumpadInputStyle.TenKey
  )
  const [acceptedNumbers, setAcceptedNumbers] = useState<string[]>(
    node.data.numpadConfig?.acceptedNumbers ?? []
  )
  const [cancelButtonText, setCancelButtonText] = useState(
    node.data.numpadConfig?.cancelButtonText ?? ''
  )
  const [newCodeInput, setNewCodeInput] = useState('')

  // Codex configuration state
  const [codexLetterCount, setCodexLetterCount] = useState(node.data.codexConfig?.letterCount ?? 4)
  const [codexAcceptedCodes, setCodexAcceptedCodes] = useState<string[]>(
    node.data.codexConfig?.acceptedCodes ?? []
  )
  const [codexCancelButtonText, setCodexCancelButtonText] = useState(
    node.data.codexConfig?.cancelButtonText ?? ''
  )
  const [newCodexCodeInput, setNewCodexCodeInput] = useState('')

  // QR Scanner configuration state
  const [qrCustomCode, setQrCustomCode] = useState(node.data.qrCodeConfig?.customCode ?? '')
  const [qrSharedCodes, setQrSharedCodes] = useState<string[]>(
    node.data.qrCodeConfig?.sharedCodes ?? []
  )
  const [qrCancelButtonText, setQrCancelButtonText] = useState(
    node.data.qrCodeConfig?.cancelButtonText ?? ''
  )
  const [newSharedCodeInput, setNewSharedCodeInput] = useState('')

  // Question/Answer configuration state
  const [qaQuestionText, setQaQuestionText] = useState(
    node.data.questionAnswerConfig?.questionText ?? ''
  )
  const [qaAnswerType, setQaAnswerType] = useState<QuestionAnswerType>(
    node.data.questionAnswerConfig?.answerType ?? QuestionAnswerType.FreeText
  )
  const [qaCorrectAnswer, setQaCorrectAnswer] = useState(
    node.data.questionAnswerConfig?.correctAnswer ?? ''
  )
  const [qaAnswerOptions, setQaAnswerOptions] = useState<AnswerOption[]>(
    node.data.questionAnswerConfig?.answerOptions ?? []
  )
  const [qaAllowSkip, setQaAllowSkip] = useState(
    node.data.questionAnswerConfig?.allowSkip ?? false
  )
  const [qaSkipButtonText, setQaSkipButtonText] = useState(
    node.data.questionAnswerConfig?.skipButtonText ?? ''
  )
  const [qaCancelButtonText, setQaCancelButtonText] = useState(
    node.data.questionAnswerConfig?.cancelButtonText ?? ''
  )
  const [newAnswerOptionText, setNewAnswerOptionText] = useState('')
  const [answerOptionError, setAnswerOptionError] = useState<string | null>(null)

  // POI Location configuration state
  const [poiDialogOpen, setPoiDialogOpen] = useState(false)
  const [poiDialogFullscreen, setPoiDialogFullscreen] = useState(false)
  const [poiMapCenterTarget, setPoiMapCenterTarget] = useState<{ latitude: number; longitude: number; trigger: number } | null>(null)
  const [poiLatitude, setPoiLatitude] = useState(
    node.data.poiLocationConfig?.latitude ?? 0
  )
  const [poiLongitude, setPoiLongitude] = useState(
    node.data.poiLocationConfig?.longitude ?? 0
  )
  const [poiDescription, setPoiDescription] = useState(
    node.data.poiLocationConfig?.description ?? ''
  )
  const [poiMarkerEmoji, setPoiMarkerEmoji] = useState(
    node.data.poiLocationConfig?.markerEmoji ?? '📍'
  )
  const [poiMarkerStyle, setPoiMarkerStyle] = useState<PoiMarkerStyle>(
    node.data.poiLocationConfig?.markerStyle ?? 'icon'
  )
  const [poiCircleRadius, setPoiCircleRadius] = useState(
    node.data.poiLocationConfig?.circleRadius ?? 50
  )
  const [poiCircleColor, setPoiCircleColor] = useState(
    node.data.poiLocationConfig?.circleColor ?? '#2196F3'
  )
  const [poiCircleBorderStyle, setPoiCircleBorderStyle] = useState<PoiCircleBorderStyle>(
    node.data.poiLocationConfig?.circleBorderStyle ?? 'solid'
  )
  const [poiCircleFillStyle, setPoiCircleFillStyle] = useState<PoiCircleFillStyle>(
    node.data.poiLocationConfig?.circleFillStyle ?? 'solid'
  )
  const [emojiPickerAnchor, setEmojiPickerAnchor] = useState<HTMLButtonElement | null>(null)
  const [emojiSearch, setEmojiSearch] = useState('')

  // Filter emojis based on search
  const filteredEmojis = useMemo(() => {
    if (!emojiSearch.trim()) {
      return EXTENDED_POI_EMOJIS
    }
    const searchLower = emojiSearch.toLowerCase().trim()
    return EXTENDED_POI_EMOJIS.filter(emoji => {
      const keywords = EMOJI_KEYWORDS[emoji] || []
      return keywords.some(keyword => keyword.includes(searchLower))
    })
  }, [emojiSearch])

  // Text Clue configuration state (legacy)
  const [textClueMessage, setTextClueMessage] = useState(
    node.data.textClueConfig?.message ?? ''
  )
  const [textCluePenaltySeconds, setTextCluePenaltySeconds] = useState(
    node.data.textClueConfig?.timePenaltySeconds ?? 0
  )

  // Clue Gate configuration state
  const [selectedClueIds, setSelectedClueIds] = useState<string[]>(
    node.data.clueGateConfig?.clueIds ?? []
  )
  const { data: adventureClues } = useAdventureClues(adventureId)
  const hasLegacyTextClue = !!(node.data.textClueConfig?.message && !node.data.clueGateConfig)

  // Audio configuration state
  const [audioConfig, setAudioConfig] = useState<AudioConfig | undefined>(
    node.data.audioConfig
  )
  const [audioUrlInput, setAudioUrlInput] = useState('')
  const [audioUrlError, setAudioUrlError] = useState<string | null>(null)
  const [isUploadingAudio, setIsUploadingAudio] = useState(false)
  const [isValidatingUrl, setIsValidatingUrl] = useState(false)
  const [audioInputMode, setAudioInputMode] = useState<'upload' | 'url'>('upload')
  const [isDraggingOver, setIsDraggingOver] = useState(false)

  // Video configuration state
  const [videoConfig, setVideoConfig] = useState<VideoConfig | undefined>(
    node.data.videoConfig
  )
  const [videoUrlInput, setVideoUrlInput] = useState('')
  const [videoUrlError, setVideoUrlError] = useState<string | null>(null)
  const [videoTitle, setVideoTitle] = useState(node.data.videoConfig?.title ?? '')

  // Image configuration state
  const [imageConfig, setImageConfig] = useState<ImageConfig | undefined>(
    node.data.imageConfig
  )
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [imageUploadError, setImageUploadError] = useState<string | null>(null)
  const [imageCaption, setImageCaption] = useState(node.data.imageConfig?.caption ?? '')
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false)

  // Unlock Adventure configuration state
  const [unlockAdventureConfig, setUnlockAdventureConfig] = useState<UnlockAdventureConfig | undefined>(
    node.data.unlockAdventureConfig
  )
  const [adventureSearchQuery, setAdventureSearchQuery] = useState('')
  const [adventureSearchResults, setAdventureSearchResults] = useState<AdventureDto[]>([])
  const [isSearchingAdventures, setIsSearchingAdventures] = useState(false)

  // Group Membership configuration state
  const [groupMembershipConfig, setGroupMembershipConfig] = useState<GroupMembershipConfig | undefined>(
    node.data.groupMembershipConfig
  )
  const [groupSearchQuery, setGroupSearchQuery] = useState('')
  const [groupSearchResults, setGroupSearchResults] = useState<GroupDto[]>([])
  const [isSearchingGroups, setIsSearchingGroups] = useState(false)
  const [myGroups, setMyGroups] = useState<GroupDto[]>([])
  const [isLoadingMyGroups, setIsLoadingMyGroups] = useState(false)

  // Gift Reward configuration state
  const [giftRewardConfig, setGiftRewardConfig] = useState<GiftRewardConfig | undefined>(
    node.data.giftRewardConfig
  )
  const [giftRewardTemplates, setGiftRewardTemplates] = useState<GiftRewardTemplateDto[]>([])
  const [isLoadingGiftRewardTemplates, setIsLoadingGiftRewardTemplates] = useState(false)

  // Exit node detection
  const isExitNode = node.data.nodeType === WorkflowNodeType.ExitSuccess ||
    node.data.nodeType === WorkflowNodeType.ExitFailed ||
    node.data.nodeType === WorkflowNodeType.ExitAbandoned

  // Formatted Message configuration - TipTap editor
  // Support both new nodeType and legacy rewardType for backward compatibility
  const isFormattedMessageReward = node.data.nodeType === WorkflowNodeType.FormattedMessage ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'formatted_message')

  const formattedMessageEditor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          // Disable Link and Underline in StarterKit since we configure them separately
          link: false,
          underline: false,
        }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        BorderlessTable.configure({ resizable: true }),
        TableRow,
        TableCell,
        TableHeader,
        ResizableImage.configure({
          inline: false,
          allowBase64: true,
        }),
        Link.configure({
          openOnClick: false,
        }),
        ImagePasteHandler.configure({
          maxWidth: 600,
          maxHeight: 600,
          quality: 0.8,
        }),
      ],
      content: node.data.formattedMessageConfig?.htmlContent || '',
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()
        updateFormattedMessageConfig({ htmlContent: html })
      },
    },
    [node.id] // Reinitialize editor when node changes
  )

  // Update formatted message config
  const updateFormattedMessageConfig = useCallback((updates: Partial<FormattedMessageConfig>) => {
    const newConfig: FormattedMessageConfig = {
      htmlContent: node.data.formattedMessageConfig?.htmlContent || '',
      ...updates,
    }
    updateNode(node.id, { formattedMessageConfig: newConfig })
  }, [node.id, node.data.formattedMessageConfig, updateNode])

  // Update editor content when node changes
  useEffect(() => {
    if (formattedMessageEditor && isFormattedMessageReward) {
      const currentContent = formattedMessageEditor.getHTML()
      const nodeContent = node.data.formattedMessageConfig?.htmlContent || ''
      if (currentContent !== nodeContent) {
        formattedMessageEditor.commands.setContent(nodeContent)
      }
    }
  }, [node.id, node.data.formattedMessageConfig?.htmlContent, formattedMessageEditor, isFormattedMessageReward])

  // Exit Message configuration - TipTap editor for exit nodes
  const exitMessageEditor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          link: false,
          underline: false,
        }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        BorderlessTable.configure({ resizable: true }),
        TableRow,
        TableCell,
        TableHeader,
        ResizableImage.configure({
          inline: false,
          allowBase64: true,
        }),
        Link.configure({
          openOnClick: false,
        }),
        ImagePasteHandler.configure({
          maxWidth: 600,
          maxHeight: 600,
          quality: 0.8,
        }),
      ],
      content: (node.data.exitMessage as string) || '',
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()
        updateNode(node.id, { exitMessage: html })
      },
    },
    [node.id]
  )

  // Update exit message editor content when node changes
  useEffect(() => {
    if (exitMessageEditor && isExitNode) {
      const currentContent = exitMessageEditor.getHTML()
      const nodeContent = (node.data.exitMessage as string) || ''
      if (currentContent !== nodeContent) {
        exitMessageEditor.commands.setContent(nodeContent)
      }
    }
  }, [node.id, node.data.exitMessage, exitMessageEditor, isExitNode])

  // Cleanup editors on unmount
  useEffect(() => {
    return () => {
      formattedMessageEditor?.destroy()
      exitMessageEditor?.destroy()
    }
  }, [formattedMessageEditor, exitMessageEditor])

  // Fullscreen state for formatted message editor
  const [isFormattedMessageFullscreen, setIsFormattedMessageFullscreen] = useState(false)

  // Fullscreen state for exit message editor
  const [isExitMessageFullscreen, setIsExitMessageFullscreen] = useState(false)

  // Measurement unit preference
  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(MeasurementUnit.Imperial)

  // Fetch user preferences for measurement unit
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await settingsApi.getUserPreferences()
        setMeasurementUnit(prefs.measurementUnit ?? MeasurementUnit.Imperial)
      } catch {
        // Default to Imperial if fetch fails
      }
    }
    loadPreferences()
  }, [])

  // Reset form when node changes
  useEffect(() => {
    setLabel(node.data.label)
    setDescription(node.data.description || '')
    setIsRequired(node.data.isRequired)
    setLoopCooldownSeconds(node.data.loopCooldownSeconds || 30)
    setSelectedLocationId(node.data.locationItemId || null)
    setSelectedLoopTargetId(node.data.loopTargetNodeId || null)
    setMaxLoops(node.data.maxLoops)
    setTimeBonusSeconds(node.data.timeBonusSeconds ?? 0)
    // Reset numpad config
    setDigitCount(node.data.numpadConfig?.digitCount ?? 4)
    setInputStyle(node.data.numpadConfig?.inputStyle ?? NumpadInputStyle.TenKey)
    setAcceptedNumbers(node.data.numpadConfig?.acceptedNumbers ?? [])
    setCancelButtonText(node.data.numpadConfig?.cancelButtonText ?? '')
    setNewCodeInput('')
    // Reset codex config
    setCodexLetterCount(node.data.codexConfig?.letterCount ?? 4)
    setCodexAcceptedCodes(node.data.codexConfig?.acceptedCodes ?? [])
    setCodexCancelButtonText(node.data.codexConfig?.cancelButtonText ?? '')
    setNewCodexCodeInput('')
    // Reset QR Scanner config
    setQrCustomCode(node.data.qrCodeConfig?.customCode ?? '')
    setQrSharedCodes(node.data.qrCodeConfig?.sharedCodes ?? [])
    setQrCancelButtonText(node.data.qrCodeConfig?.cancelButtonText ?? '')
    setNewSharedCodeInput('')
    // Reset Question/Answer config
    setQaQuestionText(node.data.questionAnswerConfig?.questionText ?? '')
    setQaAnswerType(node.data.questionAnswerConfig?.answerType ?? QuestionAnswerType.FreeText)
    setQaCorrectAnswer(node.data.questionAnswerConfig?.correctAnswer ?? '')
    setQaAnswerOptions(node.data.questionAnswerConfig?.answerOptions ?? [])
    setQaAllowSkip(node.data.questionAnswerConfig?.allowSkip ?? false)
    setQaSkipButtonText(node.data.questionAnswerConfig?.skipButtonText ?? '')
    setQaCancelButtonText(node.data.questionAnswerConfig?.cancelButtonText ?? '')
    setNewAnswerOptionText('')
    setAnswerOptionError(null)
    // Reset POI Location config
    setPoiLatitude(node.data.poiLocationConfig?.latitude ?? 0)
    setPoiLongitude(node.data.poiLocationConfig?.longitude ?? 0)
    setPoiDescription(node.data.poiLocationConfig?.description ?? '')
    setPoiMarkerEmoji(node.data.poiLocationConfig?.markerEmoji ?? '📍')
    setPoiMarkerStyle(node.data.poiLocationConfig?.markerStyle ?? 'icon')
    setPoiCircleRadius(node.data.poiLocationConfig?.circleRadius ?? 50)
    setPoiCircleColor(node.data.poiLocationConfig?.circleColor ?? '#2196F3')
    setPoiCircleBorderStyle(node.data.poiLocationConfig?.circleBorderStyle ?? 'solid')
    setPoiCircleFillStyle(node.data.poiLocationConfig?.circleFillStyle ?? 'solid')
    // Reset Text Clue config
    setTextClueMessage(node.data.textClueConfig?.message ?? '')
    // Reset Audio config
    setAudioConfig(node.data.audioConfig)
    setAudioUrlInput('')
    setAudioUrlError(null)
    // Reset Video config
    setVideoConfig(node.data.videoConfig)
    setVideoUrlInput('')
    setVideoUrlError(null)
    setVideoTitle(node.data.videoConfig?.title ?? '')
    // Reset Image config
    setImageConfig(node.data.imageConfig)
    setImageUploadError(null)
    setImageCaption(node.data.imageConfig?.caption ?? '')
    // Reset Unlock Adventure config
    setUnlockAdventureConfig(node.data.unlockAdventureConfig)
    setAdventureSearchQuery('')
    setAdventureSearchResults([])
    // Reset Group Membership config
    setGroupMembershipConfig(node.data.groupMembershipConfig)
    setGroupSearchQuery('')
    setGroupSearchResults([])
    // Reset Gift Reward config
    setGiftRewardConfig(node.data.giftRewardConfig)
  }, [node.id, node.data])

  // Fetch locations for the adventure
  const { data: locations = [] } = useQuery({
    queryKey: ['adventure-locations', adventureId],
    queryFn: async () => {
      try {
        const response = await ApiService.get<LocationItemOption[]>(
          `/api/adventures/${adventureId}/locations`
        )
        return response
      } catch {
        return []
      }
    },
    enabled: node.data.nodeType === WorkflowNodeType.LocationAction,
  })

  // Get other nodes for loop target selection
  const otherNodes = nodes.filter(
    (n) =>
      n.id !== node.id &&
      n.data.nodeType !== WorkflowNodeType.Start &&
      n.data.nodeType !== WorkflowNodeType.Exit
  )

  // Only update local state for text fields - save happens on Done click
  const handleLabelChange = (newLabel: string) => {
    setLabel(newLabel)
  }

  const handleDescriptionChange = (newDescription: string) => {
    setDescription(newDescription)
  }

  // Save all pending changes to the store/API
  const handleSave = () => {
    // In read-only mode, just close without saving
    if (isReadOnly) {
      onClose()
      return
    }

    // Collect all updates from local state
    const updates: Partial<typeof node.data> = {}

    // Basic fields
    if (label !== node.data.label) {
      updates.label = label
    }
    if ((description || undefined) !== node.data.description) {
      updates.description = description || undefined
    }

    // Numpad config - check all fields
    const numpadChanged =
      digitCount !== (node.data.numpadConfig?.digitCount ?? 4) ||
      inputStyle !== (node.data.numpadConfig?.inputStyle ?? NumpadInputStyle.TenKey) ||
      JSON.stringify(acceptedNumbers) !== JSON.stringify(node.data.numpadConfig?.acceptedNumbers ?? []) ||
      cancelButtonText !== (node.data.numpadConfig?.cancelButtonText ?? '')

    if (numpadChanged) {
      updates.numpadConfig = {
        digitCount,
        inputStyle,
        acceptedNumbers,
        cancelButtonText: cancelButtonText || undefined,
      }
    }

    // Codex config - check all fields
    const codexChanged =
      codexLetterCount !== (node.data.codexConfig?.letterCount ?? 4) ||
      JSON.stringify(codexAcceptedCodes) !== JSON.stringify(node.data.codexConfig?.acceptedCodes ?? []) ||
      codexCancelButtonText !== (node.data.codexConfig?.cancelButtonText ?? '')

    if (codexChanged) {
      updates.codexConfig = {
        letterCount: codexLetterCount,
        acceptedCodes: codexAcceptedCodes,
        cancelButtonText: codexCancelButtonText || undefined,
      }
    }

    // QR Code config - check all fields
    const qrChanged =
      qrCustomCode !== (node.data.qrCodeConfig?.customCode ?? '') ||
      JSON.stringify(qrSharedCodes) !== JSON.stringify(node.data.qrCodeConfig?.sharedCodes ?? []) ||
      qrCancelButtonText !== (node.data.qrCodeConfig?.cancelButtonText ?? '')

    if (qrChanged) {
      updates.qrCodeConfig = {
        customCode: qrCustomCode || undefined,
        sharedCodes: qrSharedCodes,
        cancelButtonText: qrCancelButtonText || undefined,
      }
    }

    // Question/Answer config - check all fields
    const qaChanged =
      qaQuestionText !== (node.data.questionAnswerConfig?.questionText ?? '') ||
      qaAnswerType !== (node.data.questionAnswerConfig?.answerType ?? QuestionAnswerType.FreeText) ||
      qaCorrectAnswer !== (node.data.questionAnswerConfig?.correctAnswer ?? '') ||
      JSON.stringify(qaAnswerOptions) !== JSON.stringify(node.data.questionAnswerConfig?.answerOptions ?? []) ||
      qaAllowSkip !== (node.data.questionAnswerConfig?.allowSkip ?? false) ||
      qaSkipButtonText !== (node.data.questionAnswerConfig?.skipButtonText ?? '') ||
      qaCancelButtonText !== (node.data.questionAnswerConfig?.cancelButtonText ?? '')

    if (qaChanged) {
      updates.questionAnswerConfig = {
        questionText: qaQuestionText,
        answerType: qaAnswerType,
        correctAnswer: qaCorrectAnswer || undefined,
        answerOptions: qaAnswerOptions.length > 0 ? qaAnswerOptions : undefined,
        allowSkip: qaAllowSkip,
        skipButtonText: qaSkipButtonText || undefined,
        cancelButtonText: qaCancelButtonText || undefined,
      }
    }

    // POI Location config - check all fields
    const poiChanged =
      poiLatitude !== (node.data.poiLocationConfig?.latitude ?? 0) ||
      poiLongitude !== (node.data.poiLocationConfig?.longitude ?? 0) ||
      poiDescription !== (node.data.poiLocationConfig?.description ?? '') ||
      poiMarkerEmoji !== (node.data.poiLocationConfig?.markerEmoji ?? '📍') ||
      poiMarkerStyle !== (node.data.poiLocationConfig?.markerStyle ?? 'icon') ||
      poiCircleRadius !== (node.data.poiLocationConfig?.circleRadius ?? 50) ||
      poiCircleColor !== (node.data.poiLocationConfig?.circleColor ?? '#2196F3') ||
      poiCircleBorderStyle !== (node.data.poiLocationConfig?.circleBorderStyle ?? 'solid') ||
      poiCircleFillStyle !== (node.data.poiLocationConfig?.circleFillStyle ?? 'solid')

    if (poiChanged) {
      updates.poiLocationConfig = {
        latitude: poiLatitude,
        longitude: poiLongitude,
        description: poiDescription || undefined,
        markerEmoji: poiMarkerEmoji,
        markerStyle: poiMarkerStyle,
        circleRadius: poiCircleRadius,
        circleColor: poiCircleColor,
        circleBorderStyle: poiCircleBorderStyle,
        circleFillStyle: poiCircleFillStyle,
      }
    }

    // Text Clue config (legacy)
    if (
      textClueMessage !== (node.data.textClueConfig?.message ?? '') ||
      textCluePenaltySeconds !== (node.data.textClueConfig?.timePenaltySeconds ?? 0)
    ) {
      updates.textClueConfig = {
        message: textClueMessage,
        timePenaltySeconds: textCluePenaltySeconds > 0 ? textCluePenaltySeconds : undefined,
      }
    }

    // Clue Gate config
    const currentClueIds = node.data.clueGateConfig?.clueIds ?? []
    if (JSON.stringify(selectedClueIds) !== JSON.stringify(currentClueIds)) {
      updates.clueGateConfig = { clueIds: selectedClueIds }
    }

    // Video config (title field)
    if (videoConfig && videoTitle !== (node.data.videoConfig?.title ?? '')) {
      updates.videoConfig = {
        ...videoConfig,
        title: videoTitle || undefined,
      }
    }

    // Only call updateNode if there are changes
    if (Object.keys(updates).length > 0) {
      updateNode(node.id, updates)
    }

    onClose()
  }

  const handleRequiredChange = (checked: boolean) => {
    setIsRequired(checked)
    updateNode(node.id, { isRequired: checked })
  }

  const handleLoopCooldownChange = (value: number) => {
    setLoopCooldownSeconds(value)
    updateNode(node.id, { loopCooldownSeconds: value })
  }

  const handleMaxLoopsChange = (value: number | undefined) => {
    setMaxLoops(value)
    updateNode(node.id, { maxLoops: value })
  }

  // Delay configuration handlers
  const updateDelayConfig = (updates: Partial<DelayConfig>) => {
    const newConfig: DelayConfig = {
      duration: delayDuration,
      unit: delayUnit,
      ...updates,
    }
    updateNode(node.id, { delayConfig: newConfig })
  }

  const handleDelayDurationChange = (_event: Event, value: number | number[]) => {
    const newValue = value as number
    setDelayDuration(newValue)
    updateDelayConfig({ duration: newValue })
  }

  const handleDelayUnitChange = (value: DelayTimeUnit) => {
    setDelayUnit(value)
    updateDelayConfig({ unit: value })
  }

  const handleTimeBonusChange = (_event: Event, value: number | number[]) => {
    const seconds = value as number
    setTimeBonusSeconds(seconds)
    updateNode(node.id, { timeBonusSeconds: seconds === 0 ? undefined : seconds })
  }

  // Format time bonus for display
  const formatTimeBonus = (seconds: number): string => {
    if (seconds === 0) return '0:00'
    const absSeconds = Math.abs(seconds)
    const mins = Math.floor(absSeconds / 60)
    const secs = absSeconds % 60
    const sign = seconds > 0 ? '+' : '-'
    return `${sign}${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleLocationChange = (_event: unknown, option: LocationItemOption | null) => {
    setSelectedLocationId(option?.id || null)
    updateNode(node.id, {
      locationItemId: option?.id,
      locationItem: option
        ? {
            id: option.id,
            name: option.name,
            quickDescription: option.quickDescription,
          }
        : undefined,
    })
  }

  const handleLoopTargetChange = (_event: unknown, option: WorkflowNode | null) => {
    setSelectedLoopTargetId(option?.id || null)
    updateNode(node.id, { loopTargetNodeId: option?.id })
  }

  // Numpad configuration handlers
  const updateNumpadConfig = (updates: Partial<NumpadConfig>) => {
    const newConfig: NumpadConfig = {
      digitCount,
      inputStyle,
      acceptedNumbers,
      cancelButtonText: cancelButtonText || undefined,
      ...updates,
    }
    updateNode(node.id, { numpadConfig: newConfig })
  }

  const handleDigitCountChange = (_event: Event, value: number | number[]) => {
    const newValue = value as number
    setDigitCount(newValue)
    updateNumpadConfig({ digitCount: newValue })
  }

  const handleInputStyleChange = (value: NumpadInputStyle) => {
    setInputStyle(value)
    updateNumpadConfig({ inputStyle: value })
  }

  const handleCancelButtonTextChange = (value: string) => {
    setCancelButtonText(value)
  }

  const handleAddAcceptedNumber = () => {
    const trimmed = newCodeInput.trim()
    // Validate: must be numeric and match digit count
    if (trimmed && /^\d+$/.test(trimmed) && trimmed.length === digitCount) {
      if (!acceptedNumbers.includes(trimmed)) {
        const newNumbers = [...acceptedNumbers, trimmed]
        setAcceptedNumbers(newNumbers)
        updateNumpadConfig({ acceptedNumbers: newNumbers })
      }
      setNewCodeInput('')
    }
  }

  const handleRemoveAcceptedNumber = (numberToRemove: string) => {
    const newNumbers = acceptedNumbers.filter(n => n !== numberToRemove)
    setAcceptedNumbers(newNumbers)
    updateNumpadConfig({ acceptedNumbers: newNumbers })
  }

  // Codex configuration handlers
  const updateCodexConfig = (updates: Partial<CodexConfig>) => {
    const newConfig: CodexConfig = {
      letterCount: codexLetterCount,
      acceptedCodes: codexAcceptedCodes,
      cancelButtonText: codexCancelButtonText || undefined,
      ...updates,
    }
    updateNode(node.id, { codexConfig: newConfig })
  }

  const handleCodexLetterCountChange = (_event: Event, value: number | number[]) => {
    const newValue = value as number
    setCodexLetterCount(newValue)
    updateCodexConfig({ letterCount: newValue })
  }

  const handleAddCodexCode = () => {
    const trimmed = newCodexCodeInput.trim()
    if (trimmed && /^[A-Z]+$/.test(trimmed) && trimmed.length === codexLetterCount) {
      if (!codexAcceptedCodes.includes(trimmed)) {
        const newCodes = [...codexAcceptedCodes, trimmed]
        setCodexAcceptedCodes(newCodes)
        updateCodexConfig({ acceptedCodes: newCodes })
      }
      setNewCodexCodeInput('')
    }
  }

  const handleRemoveCodexCode = (codeToRemove: string) => {
    const newCodes = codexAcceptedCodes.filter(c => c !== codeToRemove)
    setCodexAcceptedCodes(newCodes)
    updateCodexConfig({ acceptedCodes: newCodes })
  }

  const handleCodexCancelButtonTextChange = (value: string) => {
    setCodexCancelButtonText(value)
  }

  // QR Scanner configuration handlers
  const updateQrCodeConfig = (updates: Partial<QrCodeConfig>) => {
    const newConfig: QrCodeConfig = {
      customCode: qrCustomCode || undefined,
      sharedCodes: qrSharedCodes,
      cancelButtonText: qrCancelButtonText || undefined,
      ...updates,
    }
    updateNode(node.id, { qrCodeConfig: newConfig })
  }

  const handleQrCustomCodeChange = (value: string) => {
    setQrCustomCode(value)
  }

  const handleQrCancelButtonTextChange = (value: string) => {
    setQrCancelButtonText(value)
  }

  const handleAddSharedCode = () => {
    const trimmed = newSharedCodeInput.trim()
    if (trimmed && !qrSharedCodes.includes(trimmed)) {
      const newCodes = [...qrSharedCodes, trimmed]
      setQrSharedCodes(newCodes)
      updateQrCodeConfig({ sharedCodes: newCodes })
      setNewSharedCodeInput('')
    }
  }

  const handleGenerateSharedCode = () => {
    // Generate a random 8-character alphanumeric code
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let code = ''
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    if (!qrSharedCodes.includes(code)) {
      const newCodes = [...qrSharedCodes, code]
      setQrSharedCodes(newCodes)
      updateQrCodeConfig({ sharedCodes: newCodes })
    }
  }

  const handleRemoveSharedCode = (codeToRemove: string) => {
    const newCodes = qrSharedCodes.filter(c => c !== codeToRemove)
    setQrSharedCodes(newCodes)
    updateQrCodeConfig({ sharedCodes: newCodes })
  }

  // Question/Answer configuration handlers
  const updateQaConfig = (updates: Partial<QuestionAnswerConfig>) => {
    const newConfig: QuestionAnswerConfig = {
      questionText: qaQuestionText,
      answerType: qaAnswerType,
      correctAnswer: qaCorrectAnswer || undefined,
      answerOptions: qaAnswerOptions.length > 0 ? qaAnswerOptions : undefined,
      allowSkip: qaAllowSkip,
      skipButtonText: qaSkipButtonText || undefined,
      cancelButtonText: qaCancelButtonText || undefined,
      ...updates,
    }
    updateNode(node.id, { questionAnswerConfig: newConfig })
  }

  const handleQaQuestionTextChange = (value: string) => {
    setQaQuestionText(value)
  }

  const handleQaAnswerTypeChange = (value: QuestionAnswerType) => {
    setQaAnswerType(value)
    // Clear answer options when switching to FreeText
    if (value === QuestionAnswerType.FreeText) {
      setQaAnswerOptions([])
      updateQaConfig({ answerType: value, answerOptions: undefined })
    } else {
      updateQaConfig({ answerType: value })
    }
  }

  const handleQaCorrectAnswerChange = (value: string) => {
    setQaCorrectAnswer(value)
  }

  const handleQaAllowSkipChange = (checked: boolean) => {
    setQaAllowSkip(checked)
    updateQaConfig({ allowSkip: checked })
  }

  const handleQaSkipButtonTextChange = (value: string) => {
    setQaSkipButtonText(value)
  }

  const handleQaCancelButtonTextChange = (value: string) => {
    setQaCancelButtonText(value)
  }

  const handleAddAnswerOption = () => {
    const trimmed = newAnswerOptionText.trim()
    if (trimmed) {
      // Check for duplicate answers (case-insensitive)
      const isDuplicate = qaAnswerOptions.some(
        option => option.text.toLowerCase() === trimmed.toLowerCase()
      )
      if (isDuplicate) {
        setAnswerOptionError('This answer already exists')
        return
      }

      setAnswerOptionError(null)
      const newOption: AnswerOption = {
        id: generateOptionId(),
        text: trimmed,
        isCorrect: false,
      }
      const newOptions = [...qaAnswerOptions, newOption]
      setQaAnswerOptions(newOptions)
      updateQaConfig({ answerOptions: newOptions })
      setNewAnswerOptionText('')
    }
  }

  const handleRemoveAnswerOption = (optionId: string) => {
    const newOptions = qaAnswerOptions.filter(o => o.id !== optionId)
    setQaAnswerOptions(newOptions)
    updateQaConfig({ answerOptions: newOptions.length > 0 ? newOptions : undefined })
  }

  const handleToggleOptionCorrect = (optionId: string) => {
    const newOptions = qaAnswerOptions.map(o => {
      if (o.id === optionId) {
        return { ...o, isCorrect: !o.isCorrect }
      }
      // For MultipleChoice (single select), uncheck others when checking one
      if (qaAnswerType === QuestionAnswerType.MultipleChoice && !o.isCorrect) {
        return o
      }
      if (qaAnswerType === QuestionAnswerType.MultipleChoice) {
        return { ...o, isCorrect: false }
      }
      return o
    })
    // If using MultipleChoice and clicking an unchecked option, set it as the only correct one
    if (qaAnswerType === QuestionAnswerType.MultipleChoice) {
      const clickedOption = qaAnswerOptions.find(o => o.id === optionId)
      if (clickedOption && !clickedOption.isCorrect) {
        const updatedOptions = qaAnswerOptions.map(o => ({
          ...o,
          isCorrect: o.id === optionId
        }))
        setQaAnswerOptions(updatedOptions)
        updateQaConfig({ answerOptions: updatedOptions })
        return
      }
    }
    setQaAnswerOptions(newOptions)
    updateQaConfig({ answerOptions: newOptions })
  }

  // POI Location configuration handlers
  const updatePoiLocationConfig = (updates: Partial<PoiLocationConfig>) => {
    const newConfig: PoiLocationConfig = {
      latitude: poiLatitude,
      longitude: poiLongitude,
      description: poiDescription || undefined,
      markerEmoji: poiMarkerEmoji,
      markerStyle: poiMarkerStyle,
      circleRadius: poiCircleRadius,
      circleColor: poiCircleColor,
      circleBorderStyle: poiCircleBorderStyle,
      circleFillStyle: poiCircleFillStyle,
      ...updates,
    }
    updateNode(node.id, { poiLocationConfig: newConfig })
  }

  const handlePoiLocationChange = (location: { latitude: number; longitude: number }) => {
    setPoiLatitude(location.latitude)
    setPoiLongitude(location.longitude)
  }

  const handlePoiSave = () => {
    updatePoiLocationConfig({
      latitude: poiLatitude,
      longitude: poiLongitude,
      description: poiDescription || undefined,
      markerEmoji: poiMarkerEmoji,
      markerStyle: poiMarkerStyle,
      circleRadius: poiCircleRadius,
      circleColor: poiCircleColor,
      circleBorderStyle: poiCircleBorderStyle,
      circleFillStyle: poiCircleFillStyle,
    })
    setPoiDialogOpen(false)
  }

  const handlePoiDescriptionChange = (value: string) => {
    setPoiDescription(value)
  }

  const handlePoiMarkerEmojiChange = (emoji: string) => {
    setPoiMarkerEmoji(emoji)
    updatePoiLocationConfig({ markerEmoji: emoji })
  }

  const handlePoiMarkerStyleChange = (style: PoiMarkerStyle) => {
    setPoiMarkerStyle(style)
    updatePoiLocationConfig({ markerStyle: style })
  }

  const handlePoiCircleRadiusChange = (radius: number) => {
    setPoiCircleRadius(radius)
    updatePoiLocationConfig({ circleRadius: radius })
  }

  const handlePoiCircleColorChange = (color: string) => {
    setPoiCircleColor(color)
    updatePoiLocationConfig({ circleColor: color })
  }

  const handlePoiCircleBorderStyleChange = (style: PoiCircleBorderStyle) => {
    setPoiCircleBorderStyle(style)
    updatePoiLocationConfig({ circleBorderStyle: style })
  }

  const handlePoiCircleFillStyleChange = (style: PoiCircleFillStyle) => {
    setPoiCircleFillStyle(style)
    updatePoiLocationConfig({ circleFillStyle: style })
  }

  // Text Clue configuration handlers
  const updateTextClueConfig = (updates: Partial<TextClueConfig>) => {
    const newConfig: TextClueConfig = {
      message: textClueMessage,
      timePenaltySeconds: textCluePenaltySeconds > 0 ? textCluePenaltySeconds : undefined,
      ...updates,
    }
    updateNode(node.id, { textClueConfig: newConfig })
  }

  const handleTextClueMessageChange = (value: string) => {
    // Limit to 250 characters
    const truncated = value.slice(0, 250)
    setTextClueMessage(truncated)
  }

  const handleTextCluePenaltyChange = (value: number) => {
    // Clamp to 0-3600 seconds (max 1 hour)
    const clamped = Math.max(0, Math.min(3600, value))
    setTextCluePenaltySeconds(clamped)
  }

  // Audio configuration handlers
  const updateAudioConfig = (config: AudioConfig | undefined) => {
    setAudioConfig(config)
    updateNode(node.id, { audioConfig: config })
  }

  const validateAudioUrl = async (url: string): Promise<boolean> => {
    // Basic URL validation
    try {
      const urlObj = new URL(url)
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        setAudioUrlError('URL must use HTTP or HTTPS protocol')
        return false
      }
    } catch {
      setAudioUrlError('Please enter a valid URL')
      return false
    }

    // Check file extension
    const urlLower = url.toLowerCase()
    const hasValidExtension = SUPPORTED_AUDIO_EXTENSIONS.some(ext => urlLower.includes(ext))
    if (!hasValidExtension) {
      setAudioUrlError(`URL must point to a supported audio file (${SUPPORTED_AUDIO_EXTENSIONS.join(', ')})`)
      return false
    }

    // Try to validate that the URL returns audio content
    setIsValidatingUrl(true)
    try {
      const response = await fetch(url, { method: 'HEAD' })
      const contentType = response.headers.get('content-type')?.toLowerCase() || ''

      const isAudio = contentType.startsWith('audio/') ||
        SUPPORTED_AUDIO_MIME_TYPES.some(mime => contentType.includes(mime))

      if (!isAudio && !hasValidExtension) {
        setAudioUrlError('URL does not appear to point to an audio file')
        return false
      }

      setAudioUrlError(null)
      return true
    } catch {
      // If HEAD request fails, assume URL is valid based on extension
      // Some servers don't support HEAD requests
      setAudioUrlError(null)
      return true
    } finally {
      setIsValidatingUrl(false)
    }
  }

  const handleAudioUrlSubmit = async () => {
    if (!audioUrlInput.trim()) return

    const isValid = await validateAudioUrl(audioUrlInput.trim())
    if (isValid) {
      // Create audio config from URL
      const urlLower = audioUrlInput.toLowerCase()
      let mimeType = 'audio/mpeg'
      if (urlLower.includes('.aac')) mimeType = 'audio/aac'
      else if (urlLower.includes('.ogg')) mimeType = 'audio/ogg'
      else if (urlLower.includes('.opus')) mimeType = 'audio/opus'
      else if (urlLower.includes('.wav')) mimeType = 'audio/wav'

      const fileName = audioUrlInput.split('/').pop()?.split('?')[0] || 'audio'

      updateAudioConfig({
        audioUrl: audioUrlInput.trim(),
        fileName,
        mimeType,
      })
      setAudioUrlInput('')
    }
  }

  const processAudioFile = async (file: File) => {
    // Validate file type
    const isValidType = SUPPORTED_AUDIO_MIME_TYPES.includes(file.type as any) ||
      SUPPORTED_AUDIO_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))

    if (!isValidType) {
      setAudioUrlError(`Invalid file type. Supported formats: ${SUPPORTED_AUDIO_EXTENSIONS.join(', ')}`)
      return
    }

    // Validate file size
    if (file.size > MAX_AUDIO_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
      setAudioUrlError(`File size (${sizeMB} MB) exceeds maximum of 25 MB`)
      return
    }

    setAudioUrlError(null)
    setIsUploadingAudio(true)

    try {
      // Upload using API - need to use a saved node ID, not temp ID
      const nodeId = node.id.startsWith('temp-') ? node.id : node.id
      const result = await workflowDesignerApi.uploadNodeAudio(adventureId, nodeId, file)

      updateAudioConfig({
        mediaFileId: result.mediaFileId,
        fileName: result.fileName,
        mimeType: result.mimeType,
        fileSize: result.fileSize,
        durationSeconds: result.durationSeconds,
        audioUrl: result.audioUrl,
        metadata: result.metadata,
      })
    } catch (error: any) {
      setAudioUrlError(error.response?.data?.message || 'Failed to upload audio file')
    } finally {
      setIsUploadingAudio(false)
    }
  }

  const handleAudioFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    await processAudioFile(file)
    // Reset the file input
    event.target.value = ''
  }

  const handleAudioDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (!isUploadingAudio) {
      setIsDraggingOver(true)
    }
  }

  const handleAudioDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDraggingOver(false)
  }

  const handleAudioDrop = async (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDraggingOver(false)

    if (isUploadingAudio) return

    const file = event.dataTransfer.files?.[0]
    if (file) {
      await processAudioFile(file)
    }
  }

  const handleDeleteAudio = async () => {
    if (audioConfig?.mediaFileId && !node.id.startsWith('temp-')) {
      try {
        await workflowDesignerApi.deleteNodeAudio(adventureId, node.id)
      } catch {
        // Ignore errors - still remove from UI
      }
    }
    updateAudioConfig(undefined)
  }

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Video configuration handlers
  const updateVideoConfig = (config: VideoConfig | undefined) => {
    setVideoConfig(config)
    updateNode(node.id, { videoConfig: config })
  }

  const handleVideoUrlSubmit = () => {
    if (!videoUrlInput.trim()) return

    const validation = validateVideoUrl(videoUrlInput.trim())
    if (!validation.isValid) {
      setVideoUrlError(validation.error || 'Invalid URL')
      return
    }

    setVideoUrlError(null)
    updateVideoConfig({
      videoUrl: videoUrlInput.trim(),
      hostName: validation.hostName!,
      title: videoTitle || undefined,
    })
    setVideoUrlInput('')
  }

  const handleVideoTitleChange = (value: string) => {
    setVideoTitle(value)
  }

  const handleDeleteVideo = () => {
    updateVideoConfig(undefined)
    setVideoTitle('')
  }

  // Image configuration handlers
  const updateImageConfig = (config: ImageConfig | undefined) => {
    setImageConfig(config)
    updateNode(node.id, { imageConfig: config })
  }

  const handleImageFileSelect = async (file: File) => {
    setImageUploadError(null)
    setIsUploadingImage(true)

    try {
      const result = await workflowDesignerApi.uploadNodeImage(adventureId, node.id, file)

      // Map flat width/height to nested metadata structure
      updateImageConfig({
        mediaFileId: result.mediaFileId,
        fileName: result.fileName,
        mimeType: result.mimeType,
        fileSize: result.fileSize,
        imageUrl: result.imageUrl,
        metadata: { width: result.width, height: result.height },
        caption: imageCaption || undefined,
      })
    } catch (error: any) {
      setImageUploadError(error.response?.data?.message || 'Failed to upload image file')
    } finally {
      setIsUploadingImage(false)
    }
  }

  const handleImageCaptionChange = (value: string) => {
    setImageCaption(value)
    if (imageConfig) {
      updateImageConfig({
        ...imageConfig,
        caption: value || undefined,
      })
    }
  }

  const handleDeleteImage = async () => {
    if (imageConfig?.mediaFileId) {
      try {
        await workflowDesignerApi.deleteNodeImage(adventureId, node.id)
      } catch (error) {
        console.error('Failed to delete image:', error)
      }
    }
    updateImageConfig(undefined)
    setImageCaption('')
  }

  // Handle selecting an image from the file manager
  const handleImageFromFileManager = (file: AdventureMediaFileDto) => {
    setImageUploadError(null)
    updateImageConfig({
      mediaFileId: file.id,
      fileName: file.fileName,
      mimeType: file.mimeType,
      fileSize: file.fileSize,
      imageUrl: file.fileUrl,
      metadata: file.width && file.height ? { width: file.width, height: file.height } : undefined,
      caption: imageCaption || undefined,
    })
  }

  // Format file size for display
  const formatImageFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Get video host display name
  const getVideoHostDisplayName = (host: string): string => {
    switch (host) {
      case 'youtube.com':
      case 'youtu.be':
        return 'YouTube'
      case 'vimeo.com':
        return 'Vimeo'
      case 'dailymotion.com':
        return 'Dailymotion'
      case 'twitch.tv':
        return 'Twitch'
      default:
        return host
    }
  }

  // Unlock Adventure configuration handlers
  const updateUnlockAdventureConfig = (config: UnlockAdventureConfig | undefined) => {
    setUnlockAdventureConfig(config)
    updateNode(node.id, { unlockAdventureConfig: config })
  }

  const handleSearchMyAdventures = useCallback(async (query: string) => {
    if (!query.trim()) {
      setAdventureSearchResults([])
      return
    }

    setIsSearchingAdventures(true)
    try {
      const response = await adventuresApi.searchAdventures({
        searchTerm: query,
        myAdventuresOnly: true,
        pageSize: 10,
        page: 1,
      })
      // API returns 'adventures' array, not 'items'
      const allAdventures = (response as any).adventures || response.items || []
      // Filter out the current adventure - can't unlock the adventure you're already on
      const adventures = allAdventures.filter((adv: AdventureDto) => adv.id !== adventureId)
      setAdventureSearchResults(adventures)
    } catch (error) {
      console.error('Failed to search adventures:', error)
      setAdventureSearchResults([])
    } finally {
      setIsSearchingAdventures(false)
    }
  }, [adventureId])

  const handleSelectAdventure = (adventure: AdventureDto) => {
    const isPrivate = adventure.isPublic === false
    updateUnlockAdventureConfig({
      adventureId: adventure.id,
      adventureTitle: adventure.title,
      adventureIsPrivate: isPrivate,
      adventureDifficulty: adventure.difficulty || (adventure as any).difficultyLevel,
      adventureStatus: adventure.status,
      unlockStyle: isPrivate ? UnlockStyle.GrantAccess : UnlockStyle.Unlock,
      criteriaBypass: CriteriaBypass.None,
    })
    setAdventureSearchQuery('')
    setAdventureSearchResults([])
  }

  const handleClearUnlockAdventure = () => {
    updateUnlockAdventureConfig(undefined)
  }

  // Group Membership configuration handlers
  const updateGroupMembershipConfig = (config: GroupMembershipConfig | undefined) => {
    setGroupMembershipConfig(config)
    updateNode(node.id, { groupMembershipConfig: config })
  }

  const handleLoadMyGroups = useCallback(async () => {
    setIsLoadingMyGroups(true)
    try {
      const groups = await socialApi.getMyGroups()
      // Filter to only groups where user is admin or owner
      const adminGroups = groups.filter(g => g.userRole !== undefined && g.userRole >= 2)
      setMyGroups(adminGroups)
    } catch (error) {
      console.error('Failed to load my groups:', error)
      setMyGroups([])
    } finally {
      setIsLoadingMyGroups(false)
    }
  }, [])

  const handleSelectGroup = (group: GroupDto) => {
    updateGroupMembershipConfig({
      groupId: group.id,
      groupName: group.name,
      groupImage: group.groupImage,
    })
    setGroupSearchQuery('')
    setGroupSearchResults([])
  }

  const handleClearGroupMembership = () => {
    updateGroupMembershipConfig(undefined)
  }

  // Gift Reward configuration handlers
  const updateGiftRewardConfig = (config: GiftRewardConfig | undefined) => {
    setGiftRewardConfig(config)
    updateNode(node.id, { giftRewardConfig: config })
  }

  const handleLoadGiftRewardTemplates = useCallback(async () => {
    // First ensure we have groups loaded
    if (myGroups.length === 0) {
      setIsLoadingMyGroups(true)
      try {
        const groups = await socialApi.getMyGroups()
        // Filter to only groups where user is admin or owner
        const adminGroups = groups.filter(g => g.userRole !== undefined && g.userRole >= 2)
        setMyGroups(adminGroups)

        // Now load templates from all admin groups
        setIsLoadingGiftRewardTemplates(true)
        const allTemplates: GiftRewardTemplateDto[] = []
        for (const group of adminGroups) {
          try {
            const result = await rewardsApi.getGroupTemplates(group.id, 1, 100)
            // Only include active templates
            allTemplates.push(...result.items.filter(t => t.isActive))
          } catch {
            // Skip groups that fail
          }
        }
        setGiftRewardTemplates(allTemplates)
      } catch (error) {
        console.error('Failed to load groups for gift rewards:', error)
        setMyGroups([])
        setGiftRewardTemplates([])
      } finally {
        setIsLoadingMyGroups(false)
        setIsLoadingGiftRewardTemplates(false)
      }
    } else {
      // Groups already loaded, just load templates
      setIsLoadingGiftRewardTemplates(true)
      try {
        const allTemplates: GiftRewardTemplateDto[] = []
        for (const group of myGroups) {
          try {
            const result = await rewardsApi.getGroupTemplates(group.id, 1, 100)
            // Only include active templates
            allTemplates.push(...result.items.filter(t => t.isActive))
          } catch {
            // Skip groups that fail
          }
        }
        setGiftRewardTemplates(allTemplates)
      } catch (error) {
        console.error('Failed to load gift reward templates:', error)
        setGiftRewardTemplates([])
      } finally {
        setIsLoadingGiftRewardTemplates(false)
      }
    }
  }, [myGroups])

  const handleSelectGiftRewardTemplate = (template: GiftRewardTemplateDto) => {
    updateGiftRewardConfig({
      templateId: template.id,
      templateTitle: template.title || undefined,
      templateDescription: template.description || undefined,
      grantCountPerVisit: giftRewardConfig?.grantCountPerVisit ?? 1,
      autoGrant: giftRewardConfig?.autoGrant ?? true,
    })
  }

  const handleClearGiftReward = () => {
    updateGiftRewardConfig(undefined)
  }

  // Helper to check reward types - support both new nodeType and legacy rewardType for backward compatibility
  const isLocationReward = node.data.nodeType === WorkflowNodeType.PoiLocation ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'location')
  const isTextClueReward = node.data.nodeType === WorkflowNodeType.TextClue ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'text_clue')
  const isAudioReward = node.data.nodeType === WorkflowNodeType.AudioPlayer ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'audio')
  const isVideoReward = node.data.nodeType === WorkflowNodeType.VideoPlayer ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'video')
  const isImageReward = node.data.nodeType === WorkflowNodeType.ImageDisplay ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'image')
  const isTimeModifierReward = node.data.nodeType === WorkflowNodeType.TimeModifier ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'time_modifier')
  const isUnlockAdventureReward = node.data.nodeType === WorkflowNodeType.UnlockAdventure ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'unlock_adventure')
  const isGroupMembershipReward = node.data.nodeType === WorkflowNodeType.GroupMembership ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'group_membership')
  const isGiftRewardReward = node.data.nodeType === WorkflowNodeType.GiftReward ||
    (node.data.nodeType === WorkflowNodeType.LocationAction && node.data.rewardType === 'gift_reward')
  // Check if node has been saved (has a real GUID vs temp ID)
  const isNodeSaved = !node.id.startsWith('temp-')
  const hasPoiLocation = poiLatitude !== 0 || poiLongitude !== 0

  // Get all other POI locations for display in the map dialog (excluding current node)
  const otherPoiLocations = useMemo(() => {
    return nodes
      .filter(n =>
        n.id !== node.id &&
        // Support both new PoiLocation nodeType and legacy LocationAction with rewardType='location'
        (n.data.nodeType === WorkflowNodeType.PoiLocation || n.data.rewardType === 'location') &&
        n.data.poiLocationConfig &&
        typeof n.data.poiLocationConfig.latitude === 'number' &&
        typeof n.data.poiLocationConfig.longitude === 'number' &&
        (n.data.poiLocationConfig.latitude !== 0 || n.data.poiLocationConfig.longitude !== 0)
      )
      .map(n => ({
        id: n.id,
        label: n.data.label,
        latitude: n.data.poiLocationConfig!.latitude,
        longitude: n.data.poiLocationConfig!.longitude,
        emoji: n.data.poiLocationConfig!.markerEmoji || '📍',
        description: n.data.poiLocationConfig!.description,
        markerStyle: n.data.poiLocationConfig!.markerStyle || 'icon',
        circleRadius: n.data.poiLocationConfig!.circleRadius ?? 50,
        circleColor: n.data.poiLocationConfig!.circleColor ?? '#2196F3',
        circleBorderStyle: n.data.poiLocationConfig!.circleBorderStyle ?? 'solid',
        circleFillStyle: n.data.poiLocationConfig!.circleFillStyle ?? 'solid',
      }))
  }, [nodes, node.id])

  // Calculate map center for POI dialog
  const poiDialogMapCenter = useMemo<[number, number]>(() => {
    if (hasPoiLocation) {
      return [poiLatitude, poiLongitude]
    }
    if (otherPoiLocations.length > 0) {
      return [otherPoiLocations[0].latitude, otherPoiLocations[0].longitude]
    }
    return [39.8283, -98.5795] // Center of US fallback
  }, [hasPoiLocation, poiLatitude, poiLongitude, otherPoiLocations])

  // Extract routes from workflow edges for display in the POI dialog (faded context)
  const poiDialogRoutes = useMemo<PoiDialogRouteData[]>(() => {
    return edges
      .filter(edge => edge.data?.isRouteEdge && (edge.data?.routeConfig as any)?.cachedRoute)
      .map(edge => {
        const routeConfig = edge.data!.routeConfig as RouteConfig
        const coordinates = (routeConfig as any).cachedRoute!.coordinates as [number, number][]
        return {
          id: edge.id,
          coordinates,
          color: routeConfig.lineColor || '#9C27B0',
          style: routeConfig.lineStyle || 'dashed',
          opacity: (routeConfig.lineOpacity ?? 0.5) * 0.5, // Faded (half opacity)
          arrowStyle: routeConfig.arrowStyle || 'none',
        }
      })
  }, [edges])

  const handleDelete = () => {
    // Block deletion in read-only mode
    if (isReadOnly) return
    deleteNode(node.id)
    onClose()
  }

  const nodeColor = getNodeTypeColor(node.data.nodeType)
  const nodeTypeLabel = getNodeTypeLabel(node.data.nodeType)

  return (
    <Dialog
      open={true}
      onClose={handleSave}
      fullWidth
      maxWidth="sm"
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: isSmallScreen ? {} : {
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: nodeColor,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
        }}
      >
        <Box>
          <Typography variant="subtitle1" component="span">
            Edit Node
          </Typography>
          <Chip
            label={nodeTypeLabel}
            size="small"
            sx={{
              ml: 1,
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
            }}
          />
        </Box>
        <Button
          size="small"
          onClick={handleSave}
          sx={{ color: 'white', minWidth: 'auto' }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Read-only banner */}
        {isReadOnly && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: 'warning.light',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" fontWeight={500}>
              Read-only mode — This adventure is published. Node properties cannot be modified.
            </Typography>
          </Box>
        )}

        {/* Label */}
        <TextField
          fullWidth
          label="Label"
          value={label}
          onChange={(e) => handleLabelChange(e.target.value)}
          size="small"
          sx={{ mb: 2, mt: 1 }}
          disabled={isReadOnly}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          size="small"
          multiline
          rows={2}
          sx={{ mb: 2 }}
          disabled={isReadOnly}
        />

        {/* Exit Message - rich text editor for exit nodes */}
        {isExitNode && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7, mb: 2 } : { mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Exit Message
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Rich text message displayed to the player when they reach this exit. Supports formatting, links, and images.
            </Typography>

            {!isExitMessageFullscreen && (
              <Box className="rich-editor-container rich-editor-compact">
                {exitMessageEditor && (
                  <TipTapToolbar
                    editor={exitMessageEditor}
                    onFullscreen={() => setIsExitMessageFullscreen(true)}
                    isFullscreen={false}
                  />
                )}
                <EditorContent editor={exitMessageEditor} className="tiptap-editor tiptap-editor-compact" />
                {exitMessageEditor && <ImageBubbleMenu editor={exitMessageEditor} />}
              </Box>
            )}

            {/* Fullscreen Editor Dialog */}
            <Dialog
              open={isExitMessageFullscreen}
              onClose={() => setIsExitMessageFullscreen(false)}
              fullScreen
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Typography variant="h6">Edit Exit Message</Typography>
                <IconButton onClick={() => setIsExitMessageFullscreen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box className="rich-editor-container" sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: 'none', borderRadius: 0 }}>
                  {exitMessageEditor && (
                    <TipTapToolbar
                      editor={exitMessageEditor}
                      onFullscreen={() => setIsExitMessageFullscreen(false)}
                      isFullscreen={true}
                    />
                  )}
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <EditorContent editor={exitMessageEditor} className="tiptap-editor" />
                  </Box>
                  {exitMessageEditor && <ImageBubbleMenu editor={exitMessageEditor} />}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Button onClick={() => setIsExitMessageFullscreen(false)} variant="contained">
                  Done
                </Button>
              </Box>
            </Dialog>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* POI Location reward configuration */}
        {isLocationReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              POI Location
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              This location will be displayed on the user's map during the adventure
            </Typography>

            {hasPoiLocation ? (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h5" component="span">{poiMarkerEmoji}</Typography>
                  <Box>
                    <Typography variant="body2">
                      {poiLatitude.toFixed(6)}, {poiLongitude.toFixed(6)}
                    </Typography>
                    {poiDescription && (
                      <Typography variant="caption" color="text.secondary">
                        {poiDescription}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                No location set
              </Typography>
            )}

            <Button
              variant="outlined"
              onClick={() => setPoiDialogOpen(true)}
              fullWidth
              sx={{ mb: 2 }}
            >
              {hasPoiLocation ? 'Edit POI Location' : 'Set POI Location'}
            </Button>

            {/* Marker Style Toggle */}
            <Typography variant="subtitle2" gutterBottom>
              Marker Style
            </Typography>
            <ToggleButtonGroup
              value={poiMarkerStyle}
              exclusive
              onChange={(_, value) => value && handlePoiMarkerStyleChange(value as PoiMarkerStyle)}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="icon" sx={{ px: 2 }}>
                Icon
              </ToggleButton>
              <ToggleButton value="circle" sx={{ px: 2 }}>
                Circle
              </ToggleButton>
              <ToggleButton value="none" sx={{ px: 2 }}>
                None
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Icon Marker Configuration */}
            {poiMarkerStyle === 'icon' && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Marker Icon
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, alignItems: 'center' }}>
                  {/* Quick access emojis */}
                  {POI_MARKER_EMOJIS.map((item) => (
                    <ToggleButton
                      key={item.emoji}
                      value={item.emoji}
                      selected={poiMarkerEmoji === item.emoji}
                      onChange={() => handlePoiMarkerEmojiChange(item.emoji)}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: '1.25rem',
                      }}
                      title={item.label}
                    >
                      {item.emoji}
                    </ToggleButton>
                  ))}
                  {/* More emojis button */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => setEmojiPickerAnchor(e.currentTarget)}
                    startIcon={<EmojiEmotionsIcon />}
                    sx={{ height: 40, ml: 1 }}
                  >
                    More
                  </Button>
                </Box>

                {/* Extended Emoji Picker Popover */}
                <Popover
                  open={Boolean(emojiPickerAnchor)}
                  anchorEl={emojiPickerAnchor}
                  onClose={() => {
                    setEmojiPickerAnchor(null)
                    setEmojiSearch('')
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Box sx={{ width: 320, p: 1.5 }}>
                    {/* Search Input */}
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search emojis..."
                      value={emojiSearch}
                      onChange={(e) => setEmojiSearch(e.target.value)}
                      sx={{ mb: 1.5 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">🔍</InputAdornment>
                        ),
                      }}
                    />

                    {/* Emoji Grid */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 32px)',
                        gap: 0.5,
                        maxHeight: 280,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        justifyContent: 'center',
                      }}
                    >
                      {filteredEmojis.map((emoji, index) => (
                        <Button
                          key={`${emoji}-${index}`}
                          onClick={() => {
                            handlePoiMarkerEmojiChange(emoji)
                            setEmojiPickerAnchor(null)
                            setEmojiSearch('')
                          }}
                          sx={{
                            minWidth: 32,
                            width: 32,
                            height: 32,
                            p: 0,
                            fontSize: '1.25rem',
                            borderRadius: 1,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            ...(poiMarkerEmoji === emoji && {
                              bgcolor: 'primary.light',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }),
                          }}
                        >
                          {emoji}
                        </Button>
                      ))}
                    </Box>

                    {filteredEmojis.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No emojis found for "{emojiSearch}"
                      </Typography>
                    )}
                  </Box>
                </Popover>
              </>
            )}

            {/* Circle Marker Configuration */}
            {poiMarkerStyle === 'circle' && (
              <Box sx={{ mb: 2 }}>
                {/* Circle Radius */}
                <Typography variant="subtitle2" gutterBottom>
                  Radius ({measurementUnit === MeasurementUnit.Imperial ? 'feet' : 'meters'})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Slider
                    value={measurementUnit === MeasurementUnit.Imperial
                      ? Math.round(poiCircleRadius * 3.28084)
                      : poiCircleRadius}
                    onChange={(_, value) => {
                      const metersValue = measurementUnit === MeasurementUnit.Imperial
                        ? Math.round((value as number) / 3.28084)
                        : (value as number)
                      handlePoiCircleRadiusChange(metersValue)
                    }}
                    min={measurementUnit === MeasurementUnit.Imperial ? 30 : 10}
                    max={measurementUnit === MeasurementUnit.Imperial ? 1640 : 500}
                    step={measurementUnit === MeasurementUnit.Imperial ? 10 : 10}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={measurementUnit === MeasurementUnit.Imperial
                      ? Math.round(poiCircleRadius * 3.28084)
                      : poiCircleRadius}
                    onChange={(e) => {
                      const inputValue = parseInt(e.target.value) || 0
                      const metersValue = measurementUnit === MeasurementUnit.Imperial
                        ? Math.round(inputValue / 3.28084)
                        : inputValue
                      const clampedMeters = Math.max(10, Math.min(500, metersValue))
                      handlePoiCircleRadiusChange(clampedMeters)
                    }}
                    inputProps={{
                      min: measurementUnit === MeasurementUnit.Imperial ? 30 : 10,
                      max: measurementUnit === MeasurementUnit.Imperial ? 1640 : 500
                    }}
                    sx={{ width: 80 }}
                  />
                </Box>

                {/* Circle Color */}
                <Typography variant="subtitle2" gutterBottom>
                  Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
                  {['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#795548', '#607D8B'].map((color) => (
                    <ToggleButton
                      key={color}
                      value={color}
                      selected={poiCircleColor === color}
                      onChange={() => handlePoiCircleColorChange(color)}
                      size="small"
                      sx={{
                        width: 36,
                        height: 36,
                        p: 0,
                        bgcolor: color,
                        '&:hover': { bgcolor: color, opacity: 0.8 },
                        '&.Mui-selected': {
                          bgcolor: color,
                          border: '2px solid white',
                          boxShadow: `0 0 0 2px ${color}`,
                        },
                        '&.Mui-selected:hover': { bgcolor: color },
                      }}
                    />
                  ))}
                  <input
                    type="color"
                    value={poiCircleColor}
                    onChange={(e) => handlePoiCircleColorChange(e.target.value)}
                    style={{
                      width: 36,
                      height: 36,
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    title="Custom color"
                  />
                </Box>

                {/* Border Style */}
                <Typography variant="subtitle2" gutterBottom>
                  Border Style
                </Typography>
                <ToggleButtonGroup
                  value={poiCircleBorderStyle}
                  exclusive
                  onChange={(_, value) => value && handlePoiCircleBorderStyleChange(value as PoiCircleBorderStyle)}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="solid" sx={{ px: 2 }}>
                    Solid
                  </ToggleButton>
                  <ToggleButton value="dashed" sx={{ px: 2 }}>
                    Dashed
                  </ToggleButton>
                  <ToggleButton value="none" sx={{ px: 2 }}>
                    None
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Fill Style */}
                <Typography variant="subtitle2" gutterBottom>
                  Fill Style
                </Typography>
                <ToggleButtonGroup
                  value={poiCircleFillStyle}
                  exclusive
                  onChange={(_, value) => value && handlePoiCircleFillStyleChange(value as PoiCircleFillStyle)}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="solid" sx={{ px: 2 }}>
                    Solid Fill
                  </ToggleButton>
                  <ToggleButton value="gradient" sx={{ px: 2 }}>
                    Gradient
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {poiCircleFillStyle === 'gradient'
                    ? 'Transparency gradient from center to edge'
                    : 'Uniform semi-transparent fill'}
                </Typography>
              </Box>
            )}

            {/* POI Description */}
            <TextField
              fullWidth
              size="small"
              label="POI Description"
              placeholder="Description shown when user clicks the marker"
              value={poiDescription}
              onChange={(e) => handlePoiDescriptionChange(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />

            {/* POI Location Dialog */}
            <Dialog open={poiDialogOpen} onClose={() => setPoiDialogOpen(false)} maxWidth="md" fullWidth fullScreen={poiDialogFullscreen}>
              <DialogTitle>Set POI Location</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Click on the map to place the POI marker. Other POI locations are shown for reference.
                </Typography>
                <Box sx={{ mb: 2, position: 'relative', height: poiDialogFullscreen ? 'calc(100vh - 280px)' : '400px' }}>
                  <MapContainer
                    center={poiDialogMapCenter}
                    zoom={hasPoiLocation || otherPoiLocations.length > 0 ? 13 : 4}
                    height="100%"
                  >
                    <PoiMapClickHandler onLocationSelect={(lat, lng) => handlePoiLocationChange({ latitude: lat, longitude: lng })} />
                    {(otherPoiLocations.length > 0 || hasPoiLocation) && (
                      <PoiMapBoundsController
                        poiLocations={otherPoiLocations}
                        currentLocation={hasPoiLocation ? { latitude: poiLatitude, longitude: poiLongitude } : undefined}
                      />
                    )}
                    <PoiMapCenterController centerTarget={poiMapCenterTarget} />

                    {/* Route Polylines (faded context) */}
                    {poiDialogRoutes.map(route => (
                      <React.Fragment key={route.id}>
                        <Polyline
                          positions={route.coordinates}
                          pathOptions={{
                            color: route.color,
                            weight: 3,
                            opacity: route.opacity,
                            dashArray: route.style === 'dashed' ? '10, 10' :
                                      route.style === 'dotted' ? '4, 8' : undefined,
                          }}
                        />
                        {/* Arrow at end of route */}
                        {route.arrowStyle === 'end' && route.coordinates.length >= 2 && (
                          <Marker
                            position={route.coordinates[route.coordinates.length - 1]}
                            icon={createRouteArrowIcon(
                              route.color,
                              calculateRouteBearing(
                                route.coordinates[route.coordinates.length - 2],
                                route.coordinates[route.coordinates.length - 1]
                              )
                            )}
                          />
                        )}
                        {/* Arrows along the path */}
                        {route.arrowStyle === 'along' && route.coordinates.length >= 2 && (
                          <>
                            {route.coordinates
                              .filter((_, i) => i > 0 && i % Math.max(1, Math.floor(route.coordinates.length / 6)) === 0)
                              .map((coord, i) => {
                                const originalIndex = route.coordinates.indexOf(coord)
                                const prevCoord = route.coordinates[originalIndex - 1]
                                return (
                                  <Marker
                                    key={`${route.id}-arrow-${i}`}
                                    position={coord}
                                    icon={createRouteArrowIcon(route.color, calculateRouteBearing(prevCoord, coord))}
                                  />
                                )
                              })}
                            {/* Always show arrow at end */}
                            <Marker
                              position={route.coordinates[route.coordinates.length - 1]}
                              icon={createRouteArrowIcon(
                                route.color,
                                calculateRouteBearing(
                                  route.coordinates[route.coordinates.length - 2],
                                  route.coordinates[route.coordinates.length - 1]
                                )
                              )}
                            />
                          </>
                        )}
                      </React.Fragment>
                    ))}

                    {/* Other POI Location Markers (faded) */}
                    {otherPoiLocations.map(poi => (
                      poi.markerStyle === 'none' ? (
                        // Hidden location - show ghosted red X in editor
                        <Marker
                          key={poi.id}
                          position={[poi.latitude, poi.longitude]}
                          icon={createHiddenLocationIcon(false)}
                        >
                          <Popup>
                            <Typography variant="body2" sx={{ color: '#ef5350' }}>
                              {poi.label} (Hidden)
                            </Typography>
                          </Popup>
                        </Marker>
                      ) :
                      poi.markerStyle === 'circle' ? (
                        // Circle marker rendering
                        <React.Fragment key={poi.id}>
                          {poi.circleFillStyle === 'gradient' ? (
                            <>
                              {[0.8, 0.6, 0.4, 0.2].map((scale, index) => (
                                <Circle
                                  key={`${poi.id}-gradient-${index}`}
                                  center={[poi.latitude, poi.longitude]}
                                  radius={poi.circleRadius * scale}
                                  pathOptions={{
                                    color: 'transparent',
                                    fillColor: poi.circleColor,
                                    fillOpacity: (0.15 + (index * 0.1)) * 0.5, // Faded
                                    weight: 0,
                                  }}
                                />
                              ))}
                              <Circle
                                center={[poi.latitude, poi.longitude]}
                                radius={poi.circleRadius}
                                pathOptions={{
                                  color: poi.circleBorderStyle === 'none' ? 'transparent' : poi.circleColor,
                                  fillColor: 'transparent',
                                  fillOpacity: 0,
                                  weight: poi.circleBorderStyle === 'none' ? 0 : 1,
                                  dashArray: poi.circleBorderStyle === 'dashed' ? '8, 8' : undefined,
                                  opacity: 0.5,
                                }}
                              >
                                <Popup>
                                  <Typography variant="body2">{poi.label}</Typography>
                                </Popup>
                              </Circle>
                            </>
                          ) : (
                            <Circle
                              center={[poi.latitude, poi.longitude]}
                              radius={poi.circleRadius}
                              pathOptions={{
                                color: poi.circleBorderStyle === 'none' ? 'transparent' : poi.circleColor,
                                fillColor: poi.circleColor,
                                fillOpacity: 0.15,
                                weight: poi.circleBorderStyle === 'none' ? 0 : 1,
                                dashArray: poi.circleBorderStyle === 'dashed' ? '8, 8' : undefined,
                                opacity: 0.5,
                              }}
                            >
                              <Popup>
                                <Typography variant="body2">{poi.label}</Typography>
                              </Popup>
                            </Circle>
                          )}
                        </React.Fragment>
                      ) : (
                        // Icon marker rendering (faded)
                        <Marker
                          key={poi.id}
                          position={[poi.latitude, poi.longitude]}
                          icon={createPoiEmojiMarkerIcon(poi.emoji, false)}
                          opacity={0.6}
                        >
                          <Popup>
                            <Typography variant="body2">{poi.emoji} {poi.label}</Typography>
                          </Popup>
                        </Marker>
                      )
                    ))}

                    {/* Current POI Location Marker - distinctive crosshair for editing */}
                    {hasPoiLocation && (
                      <Marker
                        position={[poiLatitude, poiLongitude]}
                        icon={createCurrentEditingMarkerIcon()}
                      />
                    )}
                  </MapContainer>

                  {/* Map Control Buttons Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      zIndex: 1000,
                    }}
                  >
                    {/* Use My Location Button */}
                    <IconButton
                      onClick={() => {
                        if ('geolocation' in navigator) {
                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              const lat = position.coords.latitude
                              const lng = position.coords.longitude
                              handlePoiLocationChange({ latitude: lat, longitude: lng })
                              // Center map on the new location
                              setPoiMapCenterTarget({ latitude: lat, longitude: lng, trigger: Date.now() })
                            },
                            (error) => {
                              alert('Failed to get your location: ' + error.message)
                            }
                          )
                        } else {
                          alert('Geolocation is not supported by your browser')
                        }
                      }}
                      sx={{
                        backgroundColor: 'background.paper',
                        boxShadow: 2,
                        '&:hover': {
                          backgroundColor: 'background.paper',
                          boxShadow: 4,
                        },
                      }}
                      title="Use My Current Location"
                    >
                      <MyLocation />
                    </IconButton>

                    {/* Center on Configured POI Button */}
                    {hasPoiLocation && (
                      <IconButton
                        onClick={() => {
                          setPoiMapCenterTarget({ latitude: poiLatitude, longitude: poiLongitude, trigger: Date.now() })
                        }}
                        sx={{
                          backgroundColor: 'background.paper',
                          boxShadow: 2,
                          '&:hover': {
                            backgroundColor: 'background.paper',
                            boxShadow: 4,
                          },
                        }}
                        title="Center on Configured Location"
                      >
                        <CenterFocusStrong />
                      </IconButton>
                    )}

                    {/* Fullscreen Toggle Button */}
                    <IconButton
                      onClick={() => setPoiDialogFullscreen(!poiDialogFullscreen)}
                      sx={{
                        backgroundColor: 'background.paper',
                        boxShadow: 2,
                        '&:hover': {
                          backgroundColor: 'background.paper',
                          boxShadow: 4,
                        },
                      }}
                      title={poiDialogFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                    >
                      {poiDialogFullscreen ? <FullscreenExit /> : <Fullscreen />}
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Latitude"
                    type="number"
                    size="small"
                    value={poiLatitude}
                    onChange={(e) => setPoiLatitude(parseFloat(e.target.value) || 0)}
                    inputProps={{ step: 0.000001 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Longitude"
                    type="number"
                    size="small"
                    value={poiLongitude}
                    onChange={(e) => setPoiLongitude(parseFloat(e.target.value) || 0)}
                    inputProps={{ step: 0.000001 }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPoiDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={handlePoiSave}>
                  Save Location
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* Clue Gate reward configuration */}
        {isTextClueReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Clue Gate
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Select which adventure clues to unlock when a player reaches this node. Players can then request these clues on demand.
            </Typography>

            {hasLegacyTextClue && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'warning.50', borderColor: 'warning.main' }}>
                <Typography variant="caption" color="warning.main" fontWeight={500}>
                  Legacy TextClue detected. This node has inline clue content. Select clues below to migrate to the new Clue Gate system.
                </Typography>
              </Paper>
            )}

            <Autocomplete
              multiple
              size="small"
              options={adventureClues?.filter(c => c.isActive) ?? []}
              getOptionLabel={(option) => option.title}
              value={(adventureClues ?? []).filter(c => selectedClueIds.includes(c.id))}
              onChange={(_e, newValue) => {
                const ids = newValue.map(c => c.id)
                setSelectedClueIds(ids)
                updateNode(node.id, { clueGateConfig: { clueIds: ids } })
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Clues to Unlock"
                  placeholder="Select clues..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.title}
                    size="small"
                  />
                ))
              }
              sx={{ mb: 2 }}
            />

            {selectedClueIds.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No clues selected. Add clues in the Clues panel (Flow &gt; Clues) first.
              </Typography>
            )}
          </Box>
        )}

        {/* Audio reward configuration */}
        {isAudioReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Audio Player
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Upload an audio file or provide a URL to an audio file that will play for the user.
            </Typography>

            {/* Show save workflow message if node hasn't been saved yet */}
            {!isNodeSaved && !audioConfig?.audioUrl && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: 'warning.light',
                  borderColor: 'warning.main',
                }}
              >
                <Typography variant="body2" color="warning.dark">
                  <strong>Save workflow first</strong>
                </Typography>
                <Typography variant="caption" color="warning.dark">
                  You need to save the workflow before uploading audio files. Click "Save" in the toolbar, then return here to upload your audio.
                </Typography>
              </Paper>
            )}

            {/* Show audio player if we have audio */}
            {audioConfig?.audioUrl ? (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <AudiotrackIcon color="primary" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={audioConfig.fileName}>
                      {audioConfig.fileName || 'Audio file'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {audioConfig.durationSeconds !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDuration(audioConfig.durationSeconds)}
                        </Typography>
                      )}
                      {audioConfig.fileSize !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          • {formatFileSize(audioConfig.fileSize)}
                        </Typography>
                      )}
                      {audioConfig.metadata?.codec && (
                        <Typography variant="caption" color="text.secondary">
                          • {audioConfig.metadata.codec}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleDeleteAudio}
                    title="Remove audio"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Audio preview player */}
                <Box
                  component="audio"
                  controls
                  sx={{ width: '100%', height: 40 }}
                  src={audioConfig.audioUrl}
                >
                  Your browser does not support the audio element.
                </Box>
              </Paper>
            ) : isNodeSaved ? (
              /* Upload / URL input interface */
              <>
                {/* Toggle between upload and URL modes */}
                <ToggleButtonGroup
                  value={audioInputMode}
                  exclusive
                  onChange={(_, newMode) => newMode && setAudioInputMode(newMode)}
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="upload">
                    <CloudUploadIcon sx={{ mr: 1 }} fontSize="small" />
                    Upload File
                  </ToggleButton>
                  <ToggleButton value="url">
                    <LinkIcon sx={{ mr: 1 }} fontSize="small" />
                    Paste URL
                  </ToggleButton>
                </ToggleButtonGroup>

                {audioInputMode === 'upload' ? (
                  /* File upload interface */
                  <Box
                    component="label"
                    onDragOver={handleAudioDragOver}
                    onDragLeave={handleAudioDragLeave}
                    onDrop={handleAudioDrop}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3,
                      mb: 2,
                      borderRadius: 1,
                      bgcolor: isDraggingOver ? 'action.hover' : 'background.default',
                      border: '2px dashed',
                      borderColor: isDraggingOver ? 'primary.main' : 'divider',
                      cursor: isUploadingAudio ? 'wait' : 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <input
                      type="file"
                      hidden
                      accept={SUPPORTED_AUDIO_EXTENSIONS.join(',')}
                      onChange={handleAudioFileSelect}
                      disabled={isUploadingAudio}
                    />
                    {isUploadingAudio ? (
                      <>
                        <CircularProgress size={40} sx={{ mb: 1 }} />
                        <Typography variant="body2" gutterBottom>
                          Uploading...
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Please wait while your file is being uploaded
                        </Typography>
                      </>
                    ) : isDraggingOver ? (
                      <>
                        <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body2" color="primary" gutterBottom>
                          Drop audio file here
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" gutterBottom>
                          Click to upload or drag and drop
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          MP3, AAC, Opus, Vorbis, WAV (max 25MB)
                        </Typography>
                      </>
                    )}
                  </Box>
                ) : (
                  /* URL input interface */
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Audio URL"
                      placeholder="https://example.com/audio.mp3"
                      value={audioUrlInput}
                      onChange={(e) => {
                        setAudioUrlInput(e.target.value)
                        setAudioUrlError(null)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAudioUrlSubmit()
                        }
                      }}
                      disabled={isValidatingUrl}
                      error={!!audioUrlError}
                      helperText={audioUrlError || 'Enter a direct link to an MP3, AAC, Opus, Vorbis, or WAV file'}
                      InputProps={{
                        endAdornment: (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleAudioUrlSubmit}
                            disabled={!audioUrlInput.trim() || isValidatingUrl}
                            sx={{ ml: 1 }}
                          >
                            {isValidatingUrl ? 'Checking...' : 'Add'}
                          </Button>
                        ),
                      }}
                    />
                  </Box>
                )}

                {/* Error message */}
                {audioUrlError && audioInputMode === 'upload' && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                    {audioUrlError}
                  </Typography>
                )}
              </>
            ) : null}
          </Box>
        )}

        {/* Video reward configuration */}
        {isVideoReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Video Player
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Provide a URL to a video hosted on YouTube, Vimeo, Dailymotion, or Twitch.
            </Typography>

            {/* Show configured video */}
            {videoConfig?.videoUrl ? (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <VideoLibraryIcon color="primary" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={videoConfig.title || videoConfig.videoUrl}>
                      {videoConfig.title || 'Video'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getVideoHostDisplayName(videoConfig.hostName)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleDeleteVideo}
                    title="Remove video"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Video URL display */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={videoConfig.videoUrl}
                >
                  {videoConfig.videoUrl}
                </Typography>

                {/* Title edit */}
                <TextField
                  fullWidth
                  size="small"
                  label="Video Title (optional)"
                  placeholder="Enter a display title"
                  value={videoTitle}
                  onChange={(e) => handleVideoTitleChange(e.target.value)}
                  sx={{ mt: 2 }}
                />
              </Paper>
            ) : (
              /* URL input interface */
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Video URL"
                  placeholder="https://youtube.com/watch?v=..."
                  value={videoUrlInput}
                  onChange={(e) => {
                    setVideoUrlInput(e.target.value)
                    setVideoUrlError(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleVideoUrlSubmit()
                    }
                  }}
                  error={!!videoUrlError}
                  helperText={videoUrlError || `Supported: ${ALLOWED_VIDEO_HOSTS.join(', ')}`}
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleVideoUrlSubmit}
                        disabled={!videoUrlInput.trim()}
                        sx={{ ml: 1 }}
                      >
                        Add
                      </Button>
                    ),
                  }}
                />

                {/* Optional title input before adding */}
                <TextField
                  fullWidth
                  size="small"
                  label="Video Title (optional)"
                  placeholder="Enter a display title"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  sx={{ mt: 2 }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Image Display reward configuration */}
        {isImageReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Image Display
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Upload an image that will be displayed to users. Supported formats: JPG, PNG, WebP, GIF (max 10 MB).
            </Typography>

            {/* Not saved warning */}
            {!isNodeSaved && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 2 }}>
                Save the workflow first before uploading an image.
              </Typography>
            )}

            {/* Show configured image */}
            {imageConfig?.imageUrl ? (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                {/* Image preview */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    mb: 2,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                  }}
                >
                  <img
                    src={imageConfig.imageUrl}
                    alt={imageConfig.fileName || 'Uploaded image'}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: 300,
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </Box>

                {/* File info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <ImageIcon color="primary" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={imageConfig.fileName}>
                      {imageConfig.fileName || 'Image'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {imageConfig.fileSize ? formatImageFileSize(imageConfig.fileSize) : ''}
                      {imageConfig.metadata?.width && imageConfig.metadata?.height
                        ? ` • ${imageConfig.metadata.width}×${imageConfig.metadata.height}`
                        : ''}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleDeleteImage}
                    title="Remove image"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Caption input */}
                <TextField
                  fullWidth
                  size="small"
                  label="Caption (optional)"
                  placeholder="Enter an image caption"
                  value={imageCaption}
                  onChange={(e) => handleImageCaptionChange(e.target.value)}
                />
              </Paper>
            ) : (
              /* Upload interface */
              <Box sx={{ mb: 2 }}>
                <ImageUploadZone
                  onFileSelect={handleImageFileSelect}
                  enableCropping={true}
                  maxSizeMB={10}
                  label="Upload Image"
                  description="Drag and drop, paste, or click to select"
                  isUploading={isUploadingImage}
                  error={imageUploadError || undefined}
                  disabled={!isNodeSaved}
                  allowCamera={true}
                  onSelectFromFiles={isNodeSaved ? () => setImageSelectorOpen(true) : undefined}
                />

                {/* Caption input for when uploading */}
                <TextField
                  fullWidth
                  size="small"
                  label="Caption (optional)"
                  placeholder="Enter an image caption"
                  value={imageCaption}
                  onChange={(e) => setImageCaption(e.target.value)}
                  sx={{ mt: 2 }}
                />
              </Box>
            )}

            {/* Image File Selector Dialog */}
            <ImageFileSelectorDialog
              open={imageSelectorOpen}
              onClose={() => setImageSelectorOpen(false)}
              onSelect={handleImageFromFileManager}
              adventureId={adventureId}
              title="Select Image"
              description="Choose an existing image from your adventure files."
            />
          </Box>
        )}

        {/* Time Bonus/Penalty reward configuration */}
        {isTimeModifierReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Time Bonus / Penalty
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Adjust the user's total completion time. Negative values give a bonus (reduce time), positive values add a penalty.
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Adjustment: {formatTimeBonus(timeBonusSeconds)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {timeBonusSeconds < 0
                  ? 'Bonus: Reduces total completion time'
                  : timeBonusSeconds > 0
                  ? 'Penalty: Adds to total completion time'
                  : 'No time adjustment'}
              </Typography>
              <Slider
                value={timeBonusSeconds}
                onChange={handleTimeBonusChange}
                min={-300}
                max={300}
                step={5}
                valueLabelDisplay="auto"
                valueLabelFormat={formatTimeBonus}
                marks={[
                  { value: -300, label: '-5:00' },
                  { value: -150, label: '-2:30' },
                  { value: 0, label: '0:00' },
                  { value: 150, label: '+2:30' },
                  { value: 300, label: '+5:00' },
                ]}
                sx={{
                  '& .MuiSlider-rail': {
                    background: 'linear-gradient(to right, #4caf50 0%, #4caf50 50%, #f44336 50%, #f44336 100%)',
                    opacity: 1,
                  },
                  '& .MuiSlider-track': {
                    bgcolor: 'transparent',
                    border: 'none',
                  },
                  '& .MuiSlider-thumb': {
                    bgcolor: timeBonusSeconds < 0 ? 'success.main' : timeBonusSeconds > 0 ? 'error.main' : 'grey.500',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                This will be logged to the user's adventure journal
              </Typography>
            </Box>
          </Box>
        )}

        {/* Formatted Message reward configuration */}
        {isFormattedMessageReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Formatted Message
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Create a rich text message that will be displayed to the user. Supports formatting, links, and images.
            </Typography>

            {!isFormattedMessageFullscreen && (
              <Box className="rich-editor-container rich-editor-compact" sx={{ mb: 2 }}>
                {formattedMessageEditor && (
                  <TipTapToolbar
                    editor={formattedMessageEditor}
                    onFullscreen={() => setIsFormattedMessageFullscreen(true)}
                    isFullscreen={false}
                  />
                )}
                <EditorContent editor={formattedMessageEditor} className="tiptap-editor tiptap-editor-compact" />
                {formattedMessageEditor && <ImageBubbleMenu editor={formattedMessageEditor} />}
              </Box>
            )}

            {/* Fullscreen Editor Dialog */}
            <Dialog
              open={isFormattedMessageFullscreen}
              onClose={() => setIsFormattedMessageFullscreen(false)}
              fullScreen
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Typography variant="h6">Edit Formatted Message</Typography>
                <IconButton onClick={() => setIsFormattedMessageFullscreen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box className="rich-editor-container" sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: 'none', borderRadius: 0 }}>
                  {formattedMessageEditor && (
                    <TipTapToolbar
                      editor={formattedMessageEditor}
                      onFullscreen={() => setIsFormattedMessageFullscreen(false)}
                      isFullscreen={true}
                    />
                  )}
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <EditorContent editor={formattedMessageEditor} className="tiptap-editor" />
                  </Box>
                  {formattedMessageEditor && <ImageBubbleMenu editor={formattedMessageEditor} />}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Button onClick={() => setIsFormattedMessageFullscreen(false)} variant="contained">
                  Done
                </Button>
              </Box>
            </Dialog>
          </Box>
        )}

        {/* Unlock Adventure reward configuration */}
        {isUnlockAdventureReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Unlock Adventure
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Select an adventure that will be unlocked for the user when they reach this reward.
              Only adventures you own or moderate are shown.
            </Typography>

            {/* Show selected adventure if we have a valid one (with title) */}
            {unlockAdventureConfig?.adventureId && unlockAdventureConfig?.adventureTitle ? (
              <>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {unlockAdventureConfig.adventureTitle}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                        {unlockAdventureConfig.adventureIsPrivate && (
                          <Chip label="Private" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {unlockAdventureConfig.adventureStatus !== undefined && (
                          <Chip
                            label={getAdventureStatusLabel(unlockAdventureConfig.adventureStatus)}
                            size="small"
                            color={getAdventureStatusColor(unlockAdventureConfig.adventureStatus)}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {unlockAdventureConfig.adventureDifficulty !== undefined && (
                          <Chip
                            label={`Difficulty ${unlockAdventureConfig.adventureDifficulty}/5`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={handleClearUnlockAdventure} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>

                {/* Unlock Style - only show Grant Access option for private adventures */}
                {unlockAdventureConfig.adventureIsPrivate && (
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Unlock Style</InputLabel>
                    <Select
                      value={unlockAdventureConfig.unlockStyle ?? UnlockStyle.GrantAccess}
                      label="Unlock Style"
                      onChange={(e) => {
                        updateUnlockAdventureConfig({
                          ...unlockAdventureConfig,
                          unlockStyle: e.target.value as UnlockStyle,
                        })
                      }}
                    >
                      <MenuItem value={UnlockStyle.Unlock}>Unlock Only</MenuItem>
                      <MenuItem value={UnlockStyle.GrantAccess}>Grant Access (for private adventure)</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {unlockAdventureConfig.unlockStyle === UnlockStyle.GrantAccess
                        ? 'User will be granted access to see and join this private adventure.'
                        : 'Adventure will be unlocked but user still needs access if private.'}
                    </Typography>
                  </FormControl>
                )}

                {/* Criteria Bypass */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Criteria Bypass</InputLabel>
                  <Select
                    value={unlockAdventureConfig.criteriaBypass ?? CriteriaBypass.None}
                    label="Criteria Bypass"
                    onChange={(e) => {
                      updateUnlockAdventureConfig({
                        ...unlockAdventureConfig,
                        criteriaBypass: e.target.value as CriteriaBypass,
                      })
                    }}
                  >
                    <MenuItem value={CriteriaBypass.None}>No Bypass (must meet all criteria)</MenuItem>
                    <MenuItem value={CriteriaBypass.BypassPrivate}>Bypass Private Only</MenuItem>
                    <MenuItem value={CriteriaBypass.BypassDisplay}>Bypass Display Criteria</MenuItem>
                    <MenuItem value={CriteriaBypass.BypassJoin}>Bypass Join Criteria</MenuItem>
                    <MenuItem value={CriteriaBypass.BypassAll}>Bypass All Criteria</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {unlockAdventureConfig.criteriaBypass === CriteriaBypass.None && 'User must meet all display and join criteria to access the adventure.'}
                    {unlockAdventureConfig.criteriaBypass === CriteriaBypass.BypassPrivate && 'User can see private adventures, but must still meet display and join criteria.'}
                    {unlockAdventureConfig.criteriaBypass === CriteriaBypass.BypassDisplay && 'User can see the adventure without meeting display criteria (includes private), but must still meet join criteria.'}
                    {unlockAdventureConfig.criteriaBypass === CriteriaBypass.BypassJoin && 'User can join the adventure without meeting join criteria, but must still meet display criteria to see it.'}
                    {unlockAdventureConfig.criteriaBypass === CriteriaBypass.BypassAll && 'User can see and join the adventure without meeting any criteria.'}
                  </Typography>
                </FormControl>
              </>
            ) : (
              /* Show search when no adventure is selected or when we have an invalid config */
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search your adventures"
                  placeholder="Type to search..."
                  value={adventureSearchQuery}
                  onChange={(e) => {
                    setAdventureSearchQuery(e.target.value)
                    handleSearchMyAdventures(e.target.value)
                  }}
                  InputProps={{
                    endAdornment: isSearchingAdventures ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                />
                {adventureSearchResults.length > 0 && (
                  <Paper variant="outlined" sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {adventureSearchResults.map((adventure) => (
                        <ListItem
                          key={adventure.id}
                          component="div"
                          onClick={() => handleSelectAdventure(adventure)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <ListItemText
                            primary={adventure.title}
                            secondary={
                              <Box component="span" sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                {!adventure.isPublic && (
                                  <Chip
                                    label="Private"
                                    size="small"
                                    color="warning"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                                <Chip
                                  label={getAdventureStatusLabel(adventure.status)}
                                  size="small"
                                  color={getAdventureStatusColor(adventure.status)}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                                {(adventure.difficulty || (adventure as any).difficultyLevel) && (
                                  <Chip
                                    label={`Diff: ${adventure.difficulty || (adventure as any).difficultyLevel}/5`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
                {adventureSearchQuery && !isSearchingAdventures && adventureSearchResults.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    No adventures found. Make sure you own or moderate the adventure.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Group Membership reward configuration */}
        {isGroupMembershipReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Group Membership
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Select a group that the user will be added to when they reach this reward.
              Only groups you own or administer are available.
            </Typography>

            {groupMembershipConfig ? (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {groupMembershipConfig.groupImage ? (
                    <Box
                      component="img"
                      src={groupMembershipConfig.groupImage}
                      alt={groupMembershipConfig.groupName}
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <GroupsIcon color="action" />
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {groupMembershipConfig.groupName || 'Unknown Group'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {groupMembershipConfig.groupId}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={handleClearGroupMembership} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ) : (
              <Box sx={{ mb: 2 }}>
                {/* Load my groups button */}
                {myGroups.length === 0 && !isLoadingMyGroups && (
                  <Button
                    variant="outlined"
                    onClick={handleLoadMyGroups}
                    startIcon={<GroupsIcon />}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Load My Groups
                  </Button>
                )}

                {isLoadingMyGroups && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {/* Group list */}
                {myGroups.length > 0 && (
                  <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {myGroups.map((group) => (
                        <ListItem
                          key={group.id}
                          component="div"
                          onClick={() => handleSelectGroup(group)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            {group.groupImage ? (
                              <Box
                                component="img"
                                src={group.groupImage}
                                alt={group.name}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1,
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1,
                                  bgcolor: 'grey.200',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <GroupsIcon fontSize="small" color="action" />
                              </Box>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap>
                                {group.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {group.memberCount} members
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {myGroups.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Select a group from the list above.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Gift Reward configuration */}
        {isGiftRewardReward && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Gift Reward
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Select a reward template to distribute when the user reaches this node.
              Only templates from groups you own or administer are available.
            </Typography>

            {giftRewardConfig ? (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CardGiftcardIcon sx={{ color: 'primary.contrastText' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {giftRewardConfig.templateTitle || 'Unknown Template'}
                    </Typography>
                    {giftRewardConfig.templateDescription && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {giftRewardConfig.templateDescription}
                      </Typography>
                    )}
                  </Box>
                  <IconButton size="small" onClick={handleClearGiftReward} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Grant settings */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      type="number"
                      label="Grants per Visit"
                      value={giftRewardConfig.grantCountPerVisit ?? 1}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10)
                        if (!isNaN(val) && val >= 1 && val <= 100) {
                          updateGiftRewardConfig({
                            ...giftRewardConfig,
                            grantCountPerVisit: val,
                          })
                        }
                      }}
                      inputProps={{ min: 1, max: 100 }}
                      size="small"
                      sx={{ width: 140 }}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={giftRewardConfig.autoGrant ?? true}
                        onChange={(e) => {
                          updateGiftRewardConfig({
                            ...giftRewardConfig,
                            autoGrant: e.target.checked,
                          })
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Auto-grant reward</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {giftRewardConfig.autoGrant
                            ? 'Reward is granted immediately when node is reached'
                            : 'User must claim the reward manually'}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Paper>
            ) : (
              <Box sx={{ mb: 2 }}>
                {/* Load templates button */}
                {giftRewardTemplates.length === 0 && !isLoadingGiftRewardTemplates && (
                  <Button
                    variant="outlined"
                    onClick={handleLoadGiftRewardTemplates}
                    startIcon={<CardGiftcardIcon />}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Load Reward Templates
                  </Button>
                )}

                {isLoadingGiftRewardTemplates && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {/* Template list */}
                {giftRewardTemplates.length > 0 && (
                  <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {giftRewardTemplates.map((template) => (
                        <ListItem
                          key={template.id}
                          component="div"
                          onClick={() => handleSelectGiftRewardTemplate(template)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                bgcolor: 'primary.light',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <CardGiftcardIcon fontSize="small" sx={{ color: 'primary.contrastText' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap>
                                {template.title || 'Untitled Template'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {template.groupName} • {template.contentTypeName}
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {giftRewardTemplates.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Select a reward template from the list above.
                  </Typography>
                )}

                {giftRewardTemplates.length === 0 && !isLoadingGiftRewardTemplates && myGroups.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    No active reward templates found in your groups.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Loop configuration */}
        {node.data.nodeType === WorkflowNodeType.Loop && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Loop Settings
            </Typography>
            <TextField
              fullWidth
              label="Cooldown (seconds)"
              type="number"
              value={loopCooldownSeconds}
              onChange={(e) => handleLoopCooldownChange(parseInt(e.target.value) || 30)}
              size="small"
              inputProps={{ min: 1 }}
              helperText="Time before user can loop again"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Max Loops"
              type="number"
              value={maxLoops ?? ''}
              onChange={(e) => {
                const val = e.target.value ? parseInt(e.target.value) : undefined
                handleMaxLoopsChange(val && val > 0 ? val : undefined)
              }}
              size="small"
              inputProps={{ min: 1 }}
              helperText="Maximum loops before exiting (leave empty for unlimited)"
              sx={{ mb: 2 }}
            />
          </Box>
        )}

        {/* Delay configuration */}
        {node.data.nodeType === WorkflowNodeType.Delay && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Delay Settings
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Configure how long to wait before proceeding to the next node.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Duration: {delayDuration} {delayUnit === DelayTimeUnit.Seconds ? 'second' : delayUnit === DelayTimeUnit.Minutes ? 'minute' : delayUnit === DelayTimeUnit.Hours ? 'hour' : 'day'}{delayDuration !== 1 ? 's' : ''}
              </Typography>
              <Slider
                value={delayDuration}
                onChange={handleDelayDurationChange}
                min={1}
                max={60}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 15, label: '15' },
                  { value: 30, label: '30' },
                  { value: 45, label: '45' },
                  { value: 60, label: '60' },
                ]}
                valueLabelDisplay="auto"
                sx={{
                  color: '#9c27b0',
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#9c27b0',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#9c27b0',
                  },
                }}
              />
            </Box>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Time Unit</InputLabel>
              <Select
                value={delayUnit}
                label="Time Unit"
                onChange={(e) => handleDelayUnitChange(e.target.value as DelayTimeUnit)}
              >
                <MenuItem value={DelayTimeUnit.Seconds}>Seconds</MenuItem>
                <MenuItem value={DelayTimeUnit.Minutes}>Minutes</MenuItem>
                <MenuItem value={DelayTimeUnit.Hours}>Hours</MenuItem>
                <MenuItem value={DelayTimeUnit.Days}>Days</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Total delay: {delayDuration} {delayUnit === DelayTimeUnit.Seconds ? 'second' : delayUnit === DelayTimeUnit.Minutes ? 'minute' : delayUnit === DelayTimeUnit.Hours ? 'hour' : 'day'}{delayDuration !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {/* Timer Control configuration */}
        {node.data.nodeType === WorkflowNodeType.TimerControl && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Timer Control Settings
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Control the adventure clock. Use this to pause the timer during intentional breaks (e.g., lunch) so leaderboard times reflect actual adventure time.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={node.data.pausesTimer !== false}
                  onChange={(e) => {
                    updateNode(node.id, { pausesTimer: e.target.checked })
                  }}
                />
              }
              label={node.data.pausesTimer !== false ? 'Pause Clock' : 'Resume Clock'}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {node.data.pausesTimer !== false
                ? 'The adventure clock will pause when the player reaches this node.'
                : 'The adventure clock will resume when the player reaches this node.'}
            </Typography>
          </Box>
        )}

        {/* Route node configuration */}
        {node.data.nodeType === WorkflowNodeType.Route && (
          <RouteNodePropertiesPanel node={node} adventureId={adventureId} />
        )}

        {/* Decision node conditions - Decision nodes are now only for logical conditions */}
        {node.data.nodeType === WorkflowNodeType.Decision && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">
                Conditions ({node.data.conditions.length})
              </Typography>
              {!isReadOnly && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value=""
                    displayEmpty
                    onChange={(e) => {
                      const conditionType = e.target.value as unknown as WorkflowConditionType
                      if (conditionType) {
                        const newCondition = {
                          id: generateConditionId(),
                          conditionType,
                          config: getDefaultConditionConfig(conditionType),
                          order: node.data.conditions.length,
                        }
                        updateNode(node.id, {
                          conditions: [...node.data.conditions, newCondition],
                        })
                      }
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Typography color="text.secondary" variant="body2">Add Condition...</Typography>
                      }
                      return selected
                    }}
                  >
                    {filteredLogicalConditionTypes.map((cond) => (
                      <MenuItem key={cond.type} value={cond.type}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ minWidth: 24 }}>{getCriteriaTypeIcon(cond.type)}</Typography>
                            <Typography variant="body2">{cond.label}</Typography>
                          </Box>
                          <Chip
                            label={cond.requiresSubscription ? 'Pro' : 'Free'}
                            size="small"
                            color={cond.requiresSubscription ? 'warning' : 'success'}
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {node.data.conditions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No conditions configured. Add a condition to define requirements.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {node.data.conditions.map((condition, index) => (
                  <Box
                    key={condition.id || index}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {getConditionTypeLabel(condition.conditionType)}
                      </Typography>
                      {!isReadOnly && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const updatedConditions = node.data.conditions.filter((_, i) => i !== index)
                            updateNode(node.id, { conditions: updatedConditions })
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <ConditionConfigPanel
                      conditionType={condition.conditionType}
                      config={condition.config}
                      onChange={(newConfig) => {
                        console.log('[NodePropertiesPanel] Condition config changed:', {
                          nodeId: node.id,
                          index,
                          conditionId: condition.id,
                          conditionType: condition.conditionType,
                          oldConfig: condition.config,
                          newConfig,
                        })
                        const updatedConditions = [...node.data.conditions]
                        updatedConditions[index] = {
                          ...updatedConditions[index],
                          config: newConfig,
                        }
                        console.log('[NodePropertiesPanel] Calling updateNode with:', {
                          nodeId: node.id,
                          updatedConditions,
                        })
                        updateNode(node.id, { conditions: updatedConditions })
                      }}
                      measurementUnit={measurementUnit}
                      adventureId={adventureId}
                      isReadOnly={isReadOnly}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}

        {/* Number Pad configuration */}
        {isNumpadNode(node) && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Number Pad Settings
            </Typography>

            {/* Digit Count Slider */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Number of Digits: {digitCount}
              </Typography>
              <Slider
                value={digitCount}
                onChange={handleDigitCountChange}
                min={2}
                max={6}
                step={1}
                marks={[
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                  { value: 5, label: '5' },
                  { value: 6, label: '6' },
                ]}
                valueLabelDisplay="auto"
                disabled={acceptedNumbers.length > 0}
              />
              {acceptedNumbers.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Remove all accepted codes to change digit length
                </Typography>
              )}
            </Box>

            {/* Input Style Select */}
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Input Style</InputLabel>
              <Select
                value={inputStyle}
                label="Input Style"
                onChange={(e) => handleInputStyleChange(e.target.value as NumpadInputStyle)}
              >
                <MenuItem value={NumpadInputStyle.TenKey}>10-Key Number Pad</MenuItem>
                <MenuItem value={NumpadInputStyle.FreeInput}>Free Number Input</MenuItem>
                <MenuItem value={NumpadInputStyle.RollerDigits}>Digit Rollers (0-9)</MenuItem>
              </Select>
            </FormControl>

            {/* Cancel Button Text */}
            <TextField
              fullWidth
              size="small"
              label="Cancel Button Text"
              placeholder="Cancel"
              value={cancelButtonText}
              onChange={(e) => handleCancelButtonTextChange(e.target.value)}
              helperText="Text shown on the cancel button (leave empty for default)"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Accepted Numbers */}
            <Typography variant="subtitle2" gutterBottom>
              Accepted Codes
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Each code creates an exit point on the node
            </Typography>

            {/* Add new code input */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder={`Enter ${digitCount}-digit code`}
                value={newCodeInput}
                onChange={(e) => {
                  // Only allow digits and limit to digit count
                  const val = e.target.value.replace(/\D/g, '').slice(0, digitCount)
                  setNewCodeInput(val)
                }}
                inputProps={{
                  maxLength: digitCount,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                sx={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddAcceptedNumber()
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleAddAcceptedNumber}
                disabled={newCodeInput.length !== digitCount}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {/* List of accepted numbers */}
            {acceptedNumbers.length > 0 ? (
              <List dense sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                {acceptedNumbers.map((num, index) => (
                  <ListItem key={num} divider={index < acceptedNumbers.length - 1}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                          {num}
                        </Typography>
                      }
                      secondary={`Exit ${index + 1}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveAcceptedNumber(num)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No accepted codes configured. Add at least one code.
              </Typography>
            )}
          </Box>
        )}

        {/* Codex configuration */}
        {isCodexNode(node) && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Codex Settings
            </Typography>

            {/* Letter Count Slider */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Number of Letters: {codexLetterCount}
              </Typography>
              <Slider
                value={codexLetterCount}
                onChange={handleCodexLetterCountChange}
                min={2}
                max={6}
                step={1}
                marks={[
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                  { value: 5, label: '5' },
                  { value: 6, label: '6' },
                ]}
                valueLabelDisplay="auto"
                disabled={codexAcceptedCodes.length > 0}
              />
              {codexAcceptedCodes.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Remove all accepted codes to change letter count
                </Typography>
              )}
            </Box>

            {/* Cancel Button Text */}
            <TextField
              fullWidth
              size="small"
              label="Cancel Button Text"
              placeholder="Cancel"
              value={codexCancelButtonText}
              onChange={(e) => handleCodexCancelButtonTextChange(e.target.value)}
              helperText="Text shown on the cancel button (leave empty for default)"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Accepted Codes */}
            <Typography variant="subtitle2" gutterBottom>
              Accepted Codes
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Each code creates an exit point on the node
            </Typography>

            {/* Add new code input */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder={`Enter ${codexLetterCount}-letter code`}
                value={newCodexCodeInput}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, codexLetterCount)
                  setNewCodexCodeInput(val)
                }}
                inputProps={{
                  maxLength: codexLetterCount,
                  style: { textTransform: 'uppercase' },
                }}
                sx={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddCodexCode()
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleAddCodexCode}
                disabled={newCodexCodeInput.length !== codexLetterCount}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {/* List of accepted codes */}
            {codexAcceptedCodes.length > 0 ? (
              <List dense sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                {codexAcceptedCodes.map((code, index) => (
                  <ListItem key={code} divider={index < codexAcceptedCodes.length - 1}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                          {code}
                        </Typography>
                      }
                      secondary={`Exit ${index + 1}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveCodexCode(code)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No accepted codes configured. Add at least one code.
              </Typography>
            )}
          </Box>
        )}

        {/* QR Scanner configuration */}
        {isQrScannerNode(node) && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              QR Scanner Settings
            </Typography>

            {/* Custom Code (for manual entry) */}
            <TextField
              fullWidth
              size="small"
              label="Custom Code (Optional)"
              placeholder="Enter a custom code"
              value={qrCustomCode}
              onChange={(e) => handleQrCustomCodeChange(e.target.value)}
              helperText="Users can manually enter this code instead of scanning"
              sx={{ mb: 2 }}
            />

            {/* Cancel Button Text */}
            <TextField
              fullWidth
              size="small"
              label="Cancel Button Text"
              placeholder="Cancel"
              value={qrCancelButtonText}
              onChange={(e) => handleQrCancelButtonTextChange(e.target.value)}
              helperText="Text shown on the cancel button (leave empty for default)"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Shared Codes */}
            <Typography variant="subtitle2" gutterBottom>
              Shared Codes
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Each shared code creates an exit point on the node. These codes can be used to jump directly to this requirement if the user's state can reach it.
            </Typography>

            {/* Add new shared code input */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Enter shared code"
                value={newSharedCodeInput}
                onChange={(e) => setNewSharedCodeInput(e.target.value.toUpperCase())}
                sx={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddSharedCode()
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={handleAddSharedCode}
                disabled={!newSharedCodeInput.trim()}
              >
                <AddIcon />
              </IconButton>
            </Box>

            {/* Generate code button */}
            <Button
              size="small"
              variant="outlined"
              onClick={handleGenerateSharedCode}
              sx={{ mb: 2 }}
            >
              Generate Random Code
            </Button>

            {/* List of shared codes */}
            {qrSharedCodes.length > 0 ? (
              <List dense sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                {qrSharedCodes.map((code, index) => (
                  <ListItem key={code} divider={index < qrSharedCodes.length - 1}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                          {code}
                        </Typography>
                      }
                      secondary={`Exit ${index + 1}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveSharedCode(code)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No shared codes configured. Add or generate a code to create exits.
              </Typography>
            )}
          </Box>
        )}

        {/* Question/Answer configuration */}
        {isQuestionAnswerNode(node) && (
          <Box sx={isReadOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
            <Typography variant="subtitle2" gutterBottom>
              Question Settings
            </Typography>

            {/* Question Text */}
            <TextField
              fullWidth
              size="small"
              label="Question"
              placeholder="Enter your question"
              value={qaQuestionText}
              onChange={(e) => handleQaQuestionTextChange(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />

            {/* Answer Type */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Answer Type</InputLabel>
              <Select
                value={qaAnswerType}
                label="Answer Type"
                onChange={(e) => handleQaAnswerTypeChange(e.target.value as QuestionAnswerType)}
              >
                <MenuItem value={QuestionAnswerType.FreeText}>Free Text (Type Answer)</MenuItem>
                <MenuItem value={QuestionAnswerType.MultipleChoice}>Multiple Choice (Single Selection)</MenuItem>
                <MenuItem value={QuestionAnswerType.MultiSelect}>Multi-Select (Multiple Correct)</MenuItem>
              </Select>
            </FormControl>

            {/* Free Text Answer */}
            {qaAnswerType === QuestionAnswerType.FreeText && (
              <TextField
                fullWidth
                size="small"
                label="Correct Answer"
                placeholder="Enter the correct answer"
                value={qaCorrectAnswer}
                onChange={(e) => handleQaCorrectAnswerChange(e.target.value)}
                helperText="Case-insensitive match"
                sx={{ mb: 2 }}
              />
            )}

            {/* Multiple Choice / Multi-Select Options */}
            {(qaAnswerType === QuestionAnswerType.MultipleChoice || qaAnswerType === QuestionAnswerType.MultiSelect) && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {qaAnswerType === QuestionAnswerType.MultipleChoice
                    ? 'Add answer options and select the correct one (radio)'
                    : 'Add answer options and check all correct answers (checkbox)'}
                </Typography>

                {/* Add new option */}
                <Box sx={{ display: 'flex', gap: 1, mb: answerOptionError ? 0 : 2 }}>
                  <TextField
                    size="small"
                    placeholder="Enter answer option"
                    value={newAnswerOptionText}
                    onChange={(e) => {
                      setNewAnswerOptionText(e.target.value)
                      if (answerOptionError) setAnswerOptionError(null)
                    }}
                    error={!!answerOptionError}
                    sx={{ flex: 1 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddAnswerOption()
                      }
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleAddAnswerOption}
                    disabled={!newAnswerOptionText.trim()}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
                {answerOptionError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                    {answerOptionError}
                  </Typography>
                )}

                {/* Answer options list */}
                {qaAnswerOptions.length > 0 ? (
                  <List dense sx={{ bgcolor: 'background.default', borderRadius: 1, mb: 2 }}>
                    {qaAnswerOptions.map((option, index) => (
                      <ListItem key={option.id} divider={index < qaAnswerOptions.length - 1}>
                        {qaAnswerType === QuestionAnswerType.MultipleChoice ? (
                          <Radio
                            checked={option.isCorrect}
                            onChange={() => handleToggleOptionCorrect(option.id)}
                            size="small"
                          />
                        ) : (
                          <Checkbox
                            checked={option.isCorrect}
                            onChange={() => handleToggleOptionCorrect(option.id)}
                            size="small"
                          />
                        )}
                        <ListItemText
                          primary={option.text}
                          secondary={option.isCorrect ? 'Correct' : undefined}
                          secondaryTypographyProps={{ color: 'success.main' }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveAnswerOption(option.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                    No answer options configured. Add at least one option.
                  </Typography>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Skip Option */}
            <FormControlLabel
              control={
                <Switch
                  checked={qaAllowSkip}
                  onChange={(e) => handleQaAllowSkipChange(e.target.checked)}
                  size="small"
                />
              }
              label="Allow users to skip this question"
            />

            {qaAllowSkip && (
              <TextField
                fullWidth
                size="small"
                label="Skip Button Text"
                placeholder="Skip"
                value={qaSkipButtonText}
                onChange={(e) => handleQaSkipButtonTextChange(e.target.value)}
                helperText="Text shown on the skip button (leave empty for default)"
                sx={{ mt: 1, mb: 2 }}
              />
            )}

            {/* Cancel Button Text */}
            <TextField
              fullWidth
              size="small"
              label="Cancel Button Text"
              placeholder="Cancel"
              value={qaCancelButtonText}
              onChange={(e) => handleQaCancelButtonTextChange(e.target.value)}
              helperText="Text shown on the cancel button (leave empty for default)"
              sx={{ mt: qaAllowSkip ? 0 : 2 }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        {!isReadOnly && node.data.nodeType !== WorkflowNodeType.Start ? (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        ) : (
          <Box />
        )}
        <Button variant="contained" onClick={handleSave}>
          {isReadOnly ? 'Close' : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
